import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  Avatar,
  Checkbox,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ScienceIcon from "@mui/icons-material/Science";
import MedicationIcon from "@mui/icons-material/Medication";
import RadioIcon from "@mui/icons-material/Radio";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { toast } from "react-toastify";
import {
  listOrderSets,
  expandOrderSet,
  searchPharmacyItems,
} from "../../api/orderSetsApi";
import usePharmacyStore from "../../hooks/usePharmacyStore";
import PharmacyStoreSelect from "../../components/PharmacyStoreSelect";
import {
  searchChargeCaptureServices,
  investigationCategoryToGroupCode,
} from "../../../ipd-billing/billing-api";
import {
  createLabOrderBatchId,
  rememberLabOrderBatch,
  parseOrderedTime,
  fetchLabTestsPage,
  labReportObservationsForOrder,
  normalizeLabResultRows,
} from "../../../lab-technician/labOrdersShared";
import LabReportModal from "../../../lab-technician/LabReportModal";
import { fetchReport } from "../../../radiology-technician/api/radiologyReportApi";
import ReportContent from "../../../radiology-technician/components/ReportContent";
import { htmlIsEmpty } from "../../../radiology-technician/utils/radiologyPlaceholders";
import { formatIstDateTime } from "../../../../utils/istDateTime";
import { buildMedicationDetails, totalQuantity } from "../../../../utils/medicationOrder";

const API_BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

const apiHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
  "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
  "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
  USERNAME: sessionStorage.getItem("userName") || "",
});

// Orders care plan → medication_order group (used for pharmacy prescription cart).
const extractOrdersMedicationSchema = (structures) => {
  const ordersDef = (structures || []).find(
    (item) =>
      String(item?.name || "")
        .toLowerCase()
        .trim() === "orders",
  );
  if (!ordersDef) return null;
  const medOrder = (ordersDef.structure || []).find(
    (f) => f.field === "medication_order",
  );
  if (!medOrder) return null;
  const priorityField = (medOrder.structure || []).find(
    (f) => f.field === "priority",
  );
  const medicationObj = (medOrder.structure || []).find(
    (f) => f.field === "medication" && f.type === "object",
  );
  return {
    // [TIMELINE] The Orders structure's own id, which care_plans/ takes as care_plan_id —
    // same resolution order PatientDetailsPanel's handleAddCarePlan uses.
    carePlanId: Number(
      ordersDef.care_plan_id || ordersDef.id || ordersDef.template_id || 0,
    ),
    priorityOptions: priorityField?.options || [],
    medication: medicationObj?.structure || [],
  };
};

// [INVESTIGATIONS_API] "Test" activity's test_items table — test_name itself is already
// resolved via Power Search, so only the sibling per-row fields (test_description, emergency,
// instruction) are pulled out here for the pending-items table.
const extractTestItemSchema = (structures) => {
  const testDef = (structures || []).find(
    (item) =>
      String(item?.name || "")
        .toLowerCase()
        .trim() === "test",
  );
  if (!testDef) return null;
  const testItemsField = (testDef.structure || []).find(
    (f) => f.field === "test_items",
  );
  const columns = testItemsField?.columns || [];
  const instructionCol = columns.find((c) => c.field === "instruction");
  return {
    instructionOptions: instructionCol?.options || [],
  };
};

const getMedFieldOptions = (schema, field) => {
  const fieldDef = schema?.medication?.find((f) => f.field === field);
  return Array.isArray(fieldDef?.options) && fieldDef.options.length
    ? fieldDef.options
    : null;
};

const toLabel = (s = "") =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const todayDateTimeLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const getApiHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
  "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
  "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
  USERNAME: sessionStorage.getItem("userName") || "",
});

const TYPE_ICON = {
  Lab: <ScienceIcon sx={{ fontSize: 14 }} />,
  Pharmacy: <MedicationIcon sx={{ fontSize: 14 }} />,
  Radiology: <RadioIcon sx={{ fontSize: 14 }} />,
};

const CANCEL_ALLOWED_STATUSES = ["Ordered", "Sent", "Pending"];
// Radiology: a study can be called off right up until it has been performed —
// scheduling or acknowledging it in the RIS doesn't commit the patient to it.
// Once Completed/Reported there is an image and a report, so cancel is out.
const RADIOLOGY_CANCEL_STATUSES = [
  "Ordered",
  "Pending",
  "Scheduled",
  "Acknowledged",
  "Started",
];
// Pharmacy: cancel any time before the medicine is actually handed to the patient
// (Ordered/Pending/Sent/Processing — nurse hasn't started administering yet); once
// Dispensed, the medicine is already in use, so only Stop (discontinue) applies.
const PHARMACY_CANCEL_STATUSES = ["Ordered", "Pending", "Sent", "Processing"];
const PHARMACY_STOP_STATUSES = ["Dispensed"];

// update_ipd_prescription_status/ keeps its own status vocabulary, and it is not the one
// update_lab_test_status/ uses: posting "Cancelled" there comes back 200 with
// { status: "Failed", message: "Invalid status. Valid options are: ['Sent', 'Cancel', ...]" }.
// Same meaning, different word — translate on the way out and back on the way in, so the
// rest of this file keeps speaking one vocabulary (the lab/radiology one).
const PHARMACY_STATUS_TO_API = { Cancelled: "Cancel" };
const PHARMACY_STATUS_FROM_API = { Cancel: "Cancelled" };

const STATUS_COLORS = {
  Ordered: { bg: "#EFF6FF", color: "#1D4ED8", dot: "#3B82F6" },
  Sent: { bg: "#F0FDF4", color: "#15803D", dot: "#22C55E" },
  Scheduled: { bg: "#ECFEFF", color: "#0E7490", dot: "#06B6D4" },
  Acknowledged: { bg: "#F0F9FF", color: "#0369A1", dot: "#0EA5E9" },
  "Sample Acknowledged": { bg: "#ECFDF5", color: "#15803D", dot: "#22C55E" },
  "Barcode Generated": { bg: "#EEF2FF", color: "#4338CA", dot: "#6366F1" },
  Processing: { bg: "#FFFBEB", color: "#B45309", dot: "#F59E0B" },
  "Sample Collected": { bg: "#FEF3C7", color: "#92400E", dot: "#D97706" },
  "Sample Sent": { bg: "#ECFEFF", color: "#0E7490", dot: "#06B6D4" },
  "Sample Received": { bg: "#EFF6FF", color: "#1D4ED8", dot: "#3B82F6" },
  Started: { bg: "#FEFCE8", color: "#A16207", dot: "#EAB308" },
  "In Progress": { bg: "#FFF7ED", color: "#C2410C", dot: "#F97316" },
  Dispensed: { bg: "#F0FDF4", color: "#15803D", dot: "#22C55E" },
  Completed: { bg: "#F0FDF4", color: "#15803D", dot: "#22C55E" },
  Verified: { bg: "#F0FDFA", color: "#0F766E", dot: "#14B8A6" },
  Reported: { bg: "#F0FDFA", color: "#0F766E", dot: "#14B8A6" },
  Cancelled: { bg: "#FEF2F2", color: "#991B1B", dot: "#EF4444" },
  Stopped: { bg: "#FFFBEB", color: "#B45309", dot: "#F59E0B" },
};

const STOP_REASONS = [
  "Adverse Reaction",
  "Clinical Improvement",
  "Switch to Alternative Medication",
  "Patient Refusal",
  "Duplicate Order",
  "Other",
];

const PRIORITY_OPTIONS = ["Routine", "Urgent", "STAT", "ASAP"];

// [INVESTIGATIONS_API] The lab and radiology dashboards match priority by exact string
// (their Priority filters compare against "Routine" / "Urgent" / "STAT"), and the options
// here can come from care_plan_structures, which supplies them lowercase. Normalize on the
// way out so an order placed here is filterable there.
const toWirePriority = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "Routine";
  const lower = value.toLowerCase();
  if (lower === "stat" || lower === "asap") return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const SERVICE_TYPE_OPTIONS = [
  "All Service Types",
  "Diagnostic",
  "Procedure",
  "Imaging",
  "Therapeutic",
];
const DEPARTMENT_OPTIONS = [
  "All Departments",
  "Radiology",
  "Laboratory",
  "Microbiology",
  "Pathology",
  "Cardiology",
];

const INTERFACE_STYLE = {
  LIMS: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
  RIS: { bg: "#F0FDF4", color: "#15803D", border: "#BBF7D0" },
  External: { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
};

// The Orders tab used to open seeded with six hardcoded demo orders (ORD-20485…90 —
// CBC, Azithromycin, Chest X-Ray, LFT, Paracetamol, HRCT, all "Dr. A. Mehta"). They
// showed on every patient as that patient's own orders, and stayed whenever a loader
// below came back with an unrecognized shape, because each loader only replaces rows
// of its own type. Cancelling one then posted a made-up order_no and the server
// answered "No IPD lab tests found for admission_id: … and order_no: ORD-20488".
// Same reasoning as PREVIOUS_ORDERS below — the tab now starts empty and shows only
// what the three endpoints actually return.

// The "Previous" category has no data source of its own. It used to hold four
// hardcoded Completed demo orders (HbA1c, Metformin, USG Abdomen, TFT) which
// showed up on every patient's Order Management as if they were that patient's
// real history — removed. Completed work already arrives through the live order
// list with its own status; if a separate closed-orders endpoint ever ships,
// feed it in here and the Previous filter starts working on its own.
const PREVIOUS_ORDERS = [];

const PHARMACY_OPTIONS = [
  "Internal Pharmacy",
  "Central Pharmacy",
  "External Pharmacy",
];

const ITEM_SEARCH_DEBOUNCE_MS = 300;

// Medicine search — same delay as Care Plan's medication Autocomplete.
const MEDICINE_TYPEAHEAD_DEBOUNCE_MS = 1000;
const MEDICINE_TYPEAHEAD_MIN_CHARS = 2;

// Typed queries carry dose + form ("Calpol 1 g oral tablet"); prescribe/search
// indexes brand/molecule only — strip from the first digit onward (same as Care Plan).
const stripDoseForm = (name) =>
  String(name || "")
    .replace(/\d.*$/, "")
    .trim() || String(name || "").trim();

// [PHARMACY_RX] add_ipd_prescription/ wants a real generic on every medicine.
// The catalogue uses "—" as its no-generic placeholder (mapPharmacyItemToMedicine),
// which must never be sent as a drug name — fall back to the brand instead.
const resolveGenericName = (generic, fallbackName) => {
  const value = String(generic || "").trim();
  return value && value !== "—" && value !== "-"
    ? value
    : String(fallbackName || "").trim();
};

const titleCaseDosageForm = (raw = "") => {
  const s = String(raw).trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

// GET search_medicines/ — the drug-name typeahead, the same endpoint Care Plan's
// medication field uses (CarePlanActivityModal fetchChargeMasterOptions). SNOMED term
// search over brand + generic synonyms; it carries NO stock and no inventory item_id,
// so a picked name still has to be looked up against the pharmacy catalogue before it
// can be prescribed — see runStockLookup.
const searchMedicineTerms = async (name) => {
  const res = await axios.get(`${API_BASE}search_medicines/`, {
    headers: apiHeaders(),
    params: { name },
  });
  const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
  return rows
    .slice(0, 20)
    .map((med) => ({
      name: med?.term || "",
      conceptId: med?.conceptId ?? "",
      generic: med?.generic_name || "",
    }))
    .filter((med) => med.name);
};

// inventory/pharmacy/prescribe/search/ — carries generic/brand/stock, so OOS
// and substitute-by-generic-name both work off this shape.
const mapPharmacyItemToMedicine = (item) => {
  const id = item?.item_id ?? item?.id ?? item?.inventory_item_id ?? null;
  if (id == null) return null;
  const stockRaw =
    item?.stock ?? item?.available_quantity ?? item?.total_available_quantity;
  const stock = stockRaw == null ? null : Number(stockRaw) || 0;
  return {
    id: String(id),
    item_id: String(id),
    name:
      item?.item_name ||
      item?.name ||
      item?.brand_name ||
      item?.generic_name ||
      "Unnamed item",
    generic: item?.generic_name || "—",
    brand: item?.brand_name || "—",
    form: item?.dosage_form || item?.form || "",
    dosage_form: item?.dosage_form || item?.form || "",
    stock,
    has_stock: item?.has_stock ?? (stock != null ? stock > 0 : true),
    flags: [],
    service_code: item?.service_code || "",
    charges: item?.charges,
    fee: item?.fee,
    _raw: item,
  };
};

// [INVESTIGATIONS_API] charge-capture/services/ doesn't carry cpt/tat/instructions/interface
// today — those stay blank (existing card/table markup already hides them when falsy) until a
// follow-up API supplies them on add.
const mapMedicalServiceToInvestigationCard = (item) => {
  const id = item?.medical_service_id ?? item?.id ?? null;
  if (id == null) return null;
  return {
    id: String(id),
    medical_service_id: String(id),
    name: item?.term || item?.name || "Unnamed service",
    code:
      item?.service_code ||
      item?.code ||
      item?.snomed_code ||
      item?.conceptId ||
      item?.concept_id ||
      "",
    category:
      item?.groupName || item?.billing_category || item?.categoryChip || "",
    serviceType: item?.service_type || item?.categoryChip || "",
    cpt: "",
    tat: "",
    rate: item?.rate ?? item?.fee ?? null,
    chargeModifiable:
      item?.chargeModifiable === true || item?.charge_modifiable === true,
    quantityModifiable:
      item?.quantityModifiable !== false && item?.quantity_modifiable !== false,
    interface: item?.interface || item?.interface_type || "",
    instructions: "",
    _matchScore: Number(item?.match_score) || 0,
    _isHighQuality: Boolean(item?.is_high_quality),
    _raw: item,
  };
};

const ROUTE_OPTIONS = [
  "Oral",
  "IV",
  "IM",
  "SC",
  "Topical",
  "Inhalation",
  "Sublingual",
  "Rectal",
  "Nasal",
  "Otic",
  "Ophthalmic",
];

const PRESCRIPTION_FREQUENCY_OPTIONS = [
  "Daily",
  "Alternate Day",
  "Weekly",
  "Weekly Twice",
  "Weekly Thrice",
  "Fort Night",
  "Monthly",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

const DURATION_UNIT_OPTIONS = [
  "Hour(s)",
  "Day(s)",
  "Week(s)",
  "Month(s)",
  "Year(s)",
];

const INSTRUCTION_OPTIONS = [
  "Before Breakfast",
  "After Breakfast",
  "With Breakfast",
  "Before Lunch",
  "After Lunch",
  "With Lunch",
  "Before Dinner",
  "After Dinner",
  "With Dinner",
  "Before Food",
  "After Food",
  "With Food",
  "Empty Stomach",
  "Fasting",
  "Bedtime",
  "As Needed",
];

const INSTRUCTION_LANGUAGE_OPTIONS = [
  "English",
  "Hindi",
  "Bengali",
  "Marathi",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Gujarati",
  "Punjabi",
];

const DOSE_FIELDS = [
  { key: "morning", label: "M" },
  { key: "afternoon", label: "A" },
  { key: "evening", label: "E" },
  { key: "night", label: "N" },
];

const defaultRouteForForm = (form = "") => {
  const map = {
    Tablet: "Oral",
    Capsule: "Oral",
    Injection: "IV",
    Syrup: "Oral",
    Inhaler: "Inhalation",
    Gel: "Topical",
    Drops: "Ophthalmic",
    Cream: "Topical",
  };
  return map[form] || "Oral";
};

// The cart row's field naming (durationUnit) mapped onto the shared quantity
// maths, so the number shown here is the same one the order ships with.
// Returns "" for a regimen with no computable total — the field stays blank
// exactly as it did before rather than showing a misleading 0.
const calcTotalQuantity = (item) => {
  const quantity = totalQuantity({
    ...item,
    duration_unit: item.durationUnit || "",
  });
  return quantity === null ? "" : quantity;
};

const createCartItem = (medicine) => ({
  id: String(medicine.id || medicine.item_id),
  item_id: String(medicine.id || medicine.item_id),
  name: medicine.name,
  drug_name: medicine.name,
  form: medicine.form,
  generic: medicine.generic,
  priority: "",
  reason: "",
  route: "",
  frequency: "",
  morning: "",
  afternoon: "",
  evening: "",
  night: "",
  duration: "",
  durationUnit: "",
  instructions: "",
  remark: "",
  instructionLanguage: "",
  prescriptionAdvice: "",
  patientAdvice: "",
  prescriptionRemark: "",
});

const FLAG_STYLES = {
  Allergy: { bg: "#FEF2F2", color: "#991B1B", border: "#FECACA" },
  OOS: { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
  Generic: { bg: "#F0FDF4", color: "#15803D", border: "#BBF7D0" },
  DDI: { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
  "High Alert": { bg: "#FDF4FF", color: "#7E22CE", border: "#E9D5FF" },
  Restricted: { bg: "#FEF2F2", color: "#B91C1C", border: "#FECACA" },
  Formulary: { bg: "#ECFEFF", color: "#0E7490", border: "#A5F3FC" },
};

const getMedicineFlags = (medicine) => {
  if (Array.isArray(medicine.flags) && medicine.flags.length)
    return medicine.flags;
  const flags = [];
  if (!medicine.has_stock && medicine.stock === 0) flags.push("OOS");
  return flags;
};

// Order-set expand lines only carry item_id/item_name — map fields directly from the line.
const createCartItemFromSetLine = (line) => {
  const frequency = PRESCRIPTION_FREQUENCY_OPTIONS.includes(
    line.default_frequency_code,
  )
    ? line.default_frequency_code
    : "Daily";
  const form = titleCaseDosageForm(line.dosage_form || line.form);
  return {
    id: String(line.item_id),
    item_id: String(line.item_id),
    name: line.item_name,
    form,
    generic: line.generic_name || line.item_name,
    route: defaultRouteForForm(form),
    frequency,
    morning: line.default_quantity_per_dose ?? 1,
    afternoon: 0,
    evening: 0,
    night: 0,
    duration: line.default_days ?? 5,
    durationUnit: "Day(s)",
    instructions: "",
    remark: "",
    instructionLanguage: "",
    prescriptionAdvice: "",
    patientAdvice: "",
    prescriptionRemark: "",
  };
};

const cartFieldSx = {
  "& .MuiInputBase-input": { fontSize: "0.78rem", py: "7px" },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
};

// Full-screen Order Management drawer sits above MUI's default modal z-index
// (1300). Select menus portal to document.body at 1300, so the menu/backdrop
// opens *behind* the drawer and leaves an invisible layer that blocks every
// click — the "frozen UI after opening UOM" bug.
const ORDER_DRAWER_Z_INDEX = 1400;
const drawerSelectMenuProps = {
  disableScrollLock: true,
  sx: { zIndex: ORDER_DRAWER_Z_INDEX + 100 },
  PaperProps: { sx: { zIndex: ORDER_DRAWER_Z_INDEX + 100, maxHeight: 280 } },
  MenuListProps: { dense: true },
};

const cartBoxSx = {
  border: "1px solid #E5E7EB",
  borderRadius: "10px",
  backgroundColor: "#fff",
  overflow: "hidden",
};

// ─── Shared UI pieces ────────────────────────────────────────────────────────

const StatusChip = ({ status }) => {
  const c = STATUS_COLORS[status] || {
    bg: "#F3F4F6",
    color: "#374151",
    dot: "#9CA3AF",
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: "2px",
        borderRadius: "20px",
        backgroundColor: c.bg,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: c.dot,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 600,
          color: c.color,
          whiteSpace: "nowrap",
        }}
      >
        {status}
      </Typography>
    </Box>
  );
};

const PriorityChip = ({ priority }) => {
  const colors = {
    Routine: { border: "#d1d5db", color: "#374151" },
    Urgent: { border: "#FCA5A5", color: "#B91C1C" },
    STAT: { border: "#FCA5A5", color: "#B91C1C", bg: "#FEF2F2" },
    ASAP: { border: "#FDE68A", color: "#92400E" },
  };
  const c = colors[priority] || colors.Routine;
  return (
    <Box
      sx={{
        display: "inline-block",
        px: 1,
        py: "1px",
        border: `1px solid ${c.border}`,
        borderRadius: "4px",
        backgroundColor: c.bg || "transparent",
      }}
    >
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: c.color }}>
        {priority}
      </Typography>
    </Box>
  );
};

const TypeBadge = ({ type }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      color: "#6B7280",
    }}
  >
    {TYPE_ICON[type] || <AssignmentIcon sx={{ fontSize: 14 }} />}
    <Typography sx={{ fontSize: "0.78rem", color: "#374151" }}>
      {type}
    </Typography>
  </Box>
);

// Only rendered when the API actually tells us the interface — no client-side guess.
const InterfaceTag = ({ type }) => {
  if (!type) return null;
  const s = INTERFACE_STYLE[type] || INTERFACE_STYLE.LIMS;
  return (
    <Box
      sx={{
        display: "inline-block",
        px: 0.8,
        py: "1px",
        borderRadius: "4px",
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: s.color }}>
        {type}
      </Typography>
    </Box>
  );
};

const TATTag = ({ label }) => (
  <Box
    sx={{
      display: "inline-block",
      px: 0.8,
      py: "1px",
      borderRadius: "4px",
      backgroundColor: "#F3F4F6",
      border: "1px solid #E5E7EB",
    }}
  >
    <Typography sx={{ fontSize: "0.68rem", color: "#374151" }}>
      {label}
    </Typography>
  </Box>
);

const ColHeader = ({ children, width }) => (
  <TableCell
    sx={{
      width,
      py: 1,
      px: 1.5,
      fontSize: "0.7rem",
      fontWeight: 700,
      color: "#6B7280",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      whiteSpace: "nowrap",
      borderBottom: "1px solid #E5E7EB",
      backgroundColor: "#F9FAFB",
    }}
  >
    {children}
  </TableCell>
);

const BodyCell = ({ children, sx }) => (
  <TableCell
    sx={{
      py: 1,
      px: 1.5,
      fontSize: "0.8rem",
      color: "#1F2937",
      verticalAlign: "middle",
      ...sx,
    }}
  >
    {children}
  </TableCell>
);

const PlaceholderTab = ({ icon, title, description, action }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      color: "#9CA3AF",
    }}
  >
    <Box sx={{ mb: 1.5, opacity: 0.4 }}>{icon}</Box>
    <Typography
      sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#374151", mb: 0.5 }}
    >
      {title}
    </Typography>
    <Typography
      sx={{ fontSize: "0.8rem", color: "#9CA3AF", mb: action ? 2 : 0 }}
    >
      {description}
    </Typography>
    {action}
  </Box>
);

// ─── Stop Medication Dialog (Pharmacy) ────────────────────────────────────────

const StopDialog = ({ open, order, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");

  const handleClose = () => {
    setReason("");
    setComment("");
    onClose();
  };
  const handleConfirm = () => {
    onConfirm(order, reason, comment);
    setReason("");
    setComment("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#FEF3C7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.3,
            }}
          >
            <StopCircleOutlinedIcon sx={{ fontSize: 18, color: "#D97706" }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}
            >
              Stop Medication
            </Typography>
            {order && (
              <Typography
                sx={{ fontSize: "0.75rem", color: "#6B7280", mt: 0.2 }}
              >
                {order.orderNo || "—"} · {order.service}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, px: 3 }}>
        <Box sx={{ mt: 0.5 }}>
          <Typography
            sx={{
              fontSize: "0.82rem",
              color: "#6B7280",
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            This medicine has already been sent to nursing. Stopping will
            discontinue further doses so you can prescribe an alternative if
            needed. Nursing will be notified.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              displayEmpty
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              renderValue={(v) =>
                v || (
                  <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                    Select a reason...
                  </Typography>
                )
              }
              sx={{ fontSize: "0.85rem" }}
            >
              {STOP_REASONS.map((r) => (
                <MenuItem key={r} value={r} sx={{ fontSize: "0.85rem" }}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="Additional comments (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ "& .MuiInputBase-input": { fontSize: "0.83rem" } }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          size="small"
          variant="outlined"
          sx={{
            textTransform: "none",
            fontSize: "0.83rem",
            borderColor: "#E5E7EB",
            color: "#374151",
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!reason}
          size="small"
          variant="contained"
          sx={{
            textTransform: "none",
            fontSize: "0.83rem",
            backgroundColor: "#D97706",
            "&:hover": { backgroundColor: "#B45309" },
            "&:disabled": { backgroundColor: "#FDE68A" },
          }}
        >
          Confirm Stop
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Order Detail Panel ───────────────────────────────────────────────────────

const OrderDetailPanel = ({ order, onClose }) => {
  if (!order) return null;
  const TIMELINE = [
    "Order Created",
    "Acknowledged",
    "Sample Collected",
    "Processing",
    "Completed",
    "Result Available",
  ];
  const statusIdx = {
    Ordered: 0,
    Sent: 0,
    Scheduled: 0,
    Acknowledged: 1,
    "Sample Acknowledged": 1,
    "Barcode Generated": 1,
    "Sample Collected": 2,
    "Sample Sent": 2,
    "Sample Received": 2,
    Started: 2,
    Processing: 3,
    "In Progress": 3,
    Completed: 4,
    Verified: 4,
    Reported: 5,
    "Result Available": 5,
  };
  const current = statusIdx[order.status] ?? 0;
  return (
    <Box
      sx={{
        width: 340,
        borderLeft: "1px solid #E5E7EB",
        height: "100%",
        overflowY: "auto",
        backgroundColor: "#fff",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E7EB",
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 1,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}
        >
          Order Details
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#6B7280",
              mb: 1,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Order Information
          </Typography>
          {[
            ["Order No", order.orderNo],
            ["Type", order.type],
            ["Service", order.service],
            ["Department", order.dept || order.department],
            ["Priority", order.priority],
            ["Status", order.status],
          ].map(([k, v]) => (
            <Box
              key={k}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.5,
                borderBottom: "1px solid #F3F4F6",
              }}
            >
              <Typography sx={{ fontSize: "0.78rem", color: "#6B7280" }}>
                {k}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "#1F2937",
                  fontWeight: 500,
                  textAlign: "right",
                  maxWidth: "55%",
                }}
              >
                {v || "—"}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#6B7280",
              mb: 1,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Doctor Information
          </Typography>
          {[
            ["Ordered By", order.doctor],
            ["Ordered On", order.ordered || order.date],
          ].map(([k, v]) => (
            <Box
              key={k}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.5,
                borderBottom: "1px solid #F3F4F6",
              }}
            >
              <Typography sx={{ fontSize: "0.78rem", color: "#6B7280" }}>
                {k}
              </Typography>
              <Typography
                sx={{ fontSize: "0.78rem", color: "#1F2937", fontWeight: 500 }}
              >
                {v || "—"}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#6B7280",
              mb: 1.5,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Order Timeline
          </Typography>
          {TIMELINE.map((step, i) => {
            const done = i <= current;
            const active = i === current;
            return (
              <Box key={step} sx={{ display: "flex", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: done ? "#21cdc0" : "#E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      border: active ? "2px solid #21cdc0" : "none",
                    }}
                  >
                    {done && (
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 14, color: "#fff" }}
                      />
                    )}
                  </Box>
                  {i < TIMELINE.length - 1 && (
                    <Box
                      sx={{
                        width: 2,
                        height: 24,
                        backgroundColor:
                          done && i < current ? "#21cdc0" : "#E5E7EB",
                        mt: 0.2,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: done ? "#1F2937" : "#9CA3AF",
                    fontWeight: active ? 700 : 400,
                    pt: 0.2,
                    pb: i < TIMELINE.length - 1 ? 2 : 0,
                  }}
                >
                  {step}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Report (read-only, for the ordering doctor) ──────────────────────────────

// A diagnostic order's report only exists once the department that owns the
// work has published it — the lab bench on /laboratories (Completed/Verified),
// radiology on /radiology-services (Reported/Result Available). Until then the
// Orders tab shows "Not generated"; nobody reports from this drawer.
const REPORT_READY_STATUSES = [
  "Completed",
  "Verified",
  "Reported",
  "Result Available",
];

const REPORTABLE_TYPES = ["Lab", "Radiology"];

const isOrderReportReady = (order) =>
  REPORTABLE_TYPES.includes(order?.type) &&
  REPORT_READY_STATUSES.includes(order?.status);

// Result rows for Lab come from the lab console store (and, if needed,
// get_patient_lab_tests/). Radiology reports are the HTML snapshot saved
// from /radiology-services (fetchReport).
const LabOrderReportDialog = ({
  order,
  patientName,
  uhid,
  patient,
  onClose,
}) => {
  const isRadiology = order?.type === "Radiology";
  const isLab = order?.type === "Lab";
  const [radReport, setRadReport] = useState(null);
  const [radLoading, setRadLoading] = useState(false);
  const [labOrder, setLabOrder] = useState(order);
  const [labObservations, setLabObservations] = useState({});

  useEffect(() => {
    if (!order?.orderNo || !isRadiology) {
      setRadReport(null);
      setRadLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRadLoading(true);
    fetchReport(order.orderNo)
      .then((found) => {
        if (!cancelled) setRadReport(found);
      })
      .catch(() => {
        if (!cancelled) setRadReport(null);
      })
      .finally(() => {
        if (!cancelled) setRadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order, isRadiology]);

  useEffect(() => {
    if (!order?.orderNo || !isLab) {
      setLabOrder(order);
      setLabObservations({});
      return undefined;
    }
    setLabOrder(order);
    setLabObservations(labReportObservationsForOrder(order.orderNo, order));

    const admissionId = patient?.admissionId || patient?.id;
    if (!admissionId) return undefined;

    let cancelled = false;
    fetchLabTestsPage(
      axios,
      apiHeaders(),
      {
        admissionId,
        page: 1,
        pageSize: 50,
        patientType: "IPD",
      },
      { skipGlobalLoader: true },
    )
      .then(({ patients }) => {
        if (cancelled) return;
        const match = (patients || [])
          .flatMap((p) => p.orders || [])
          .find((o) => String(o.orderNo) === String(order.orderNo));
        if (!match) return;
        setLabOrder((prev) => ({ ...(prev || {}), ...match, type: "Lab" }));
        const next = labReportObservationsForOrder(order.orderNo, match);
        if (Object.keys(next).length > 0) setLabObservations(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [order, isLab, patient]);

  if (!order) return null;

  if (isLab) {
    return (
      <LabReportModal
        patient={{
          name: patientName,
          uhid,
          age: patient?.age,
          gender: patient?.gender,
          dob: patient?.dob,
          doctorName:
            labOrder?.doctor || patient?.doctor_name || patient?.consultant,
          patientId: patient?.patient_id || patient?.patientId,
          abhaId: patient?.abhaId,
          orders: [
            {
              ...labOrder,
              orderNo: order.orderNo,
              service: labOrder?.service || order.service,
              status: labOrder?.status || order.status,
              results:
                labOrder?.results ||
                normalizeLabResultRows(order.results) ||
                [],
            },
          ],
        }}
        observations={labObservations}
        focusOrderNo={order.orderNo}
        readOnly
        onObservationChange={() => {}}
        onClose={onClose}
      />
    );
  }

  const radHtml = radReport?.content_html || "";
  const hasRadBody = isRadiology && !htmlIsEmpty(radHtml);
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: ORDER_DRAWER_Z_INDEX + 100 }}
    >
      <DialogTitle
        sx={{
          fontSize: "0.95rem",
          fontWeight: 700,
          color: "#111827",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Report · {order.service}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 1.5,
            mb: 2,
          }}
        >
          {[
            ["Patient", `${patientName}${uhid ? ` · ${uhid}` : ""}`],
            ["Order No", order.orderNo],
            ["Department", order.dept || "—"],
            ["Ordered By", order.doctor || "—"],
            ["Ordered On", order.ordered || "—"],
            ["Status", order.status],
            ...(isRadiology && radReport?.radiologist_name
              ? [["Reported By", radReport.radiologist_name]]
              : []),
          ].map(([k, v]) => (
            <Box key={k}>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#6B7280",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {k}
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: "#1F2937", fontWeight: 500 }}
              >
                {v || "—"}
              </Typography>
            </Box>
          ))}
        </Box>
        {radLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : hasRadBody ? (
          <ReportContent html={radHtml} />
        ) : (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "#FFF7ED",
              color: "#C2410C",
              fontSize: "0.8rem",
            }}
          >
            This order has been reported, but the detailed results aren't
            available to this screen yet. Please refer to the department's
            report on the laboratory / radiology console.
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontSize: "0.8rem" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Orders Tab (Active + Previous, combined) ─────────────────────────────────

const ORDERS_FILTER_OPTIONS = ["All", "Active", "Previous"];

// Order rows reach this table with three different date shapes: ISO-ish
// created_at from the pharmacy endpoint, "MM/DD/YYYY, hh:mm:ss AM/PM" from the
// lab/radiology read endpoints, and the seeded "25 Jun, 09:12" demo strings.
// Normalize to epoch ms so a freshly placed order — lab, radiology or
// medication — always lands at the top of the list. Unparseable dates return 0
// and sink to the bottom instead of shuffling rows on every render.
const normalizeTimestampToMs = (value) => {
  if (!value) return 0;
  const raw = String(value).trim();

  // US format: MM/DD/YYYY, hh:mm:ss AM/PM (from lab/radiology APIs)
  const usDateTime =
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s*(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/i.exec(raw);
  if (usDateTime) {
    const [, mm, dd, yyyy, hh12, min, ss, ampm] = usDateTime;
    let hh = parseInt(hh12, 10) % 12;
    if (/pm/i.test(ampm)) hh += 12;
    // Create UTC date to normalize timezone handling
    return new Date(Date.UTC(+yyyy, +mm - 1, +dd, hh, +min, +ss)).getTime();
  }

  // ISO format with timezone: YYYY-MM-DDTHH:mm:ss+/-HH:mm (from pharmacy API)
  // Date.parse handles this correctly
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const orderedAtMs = normalizeTimestampToMs;

// The same three shapes, rendered instead of sorted. Both the pharmacy's
// created_at and the lab/radiology "MM/DD/YYYY, hh:mm:ss AM/PM" are UTC with
// the marker lost, so printing them verbatim showed an order placed at 20:00
// IST as 14:30 — the ward, the pharmacy and this tab disagreed about one
// order. parseOrderedTime restores the marker on the US shape; parseInstant
// (inside formatIstDateTime) does it for the ISO shape. The seeded demo
// strings ("25 Jun, 09:12") parse as neither and are passed through unchanged.
const orderedLabel = (value) => {
  if (!value) return "—";
  return formatIstDateTime(parseOrderedTime(value), String(value));
};

const OrdersTab = ({
  activeOrders,
  previousOrders,
  onCancelClick,
  onStopClick,
  onViewClick,
  onViewReport,
  onStatusChange,
}) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");

  // Newest placement first, regardless of which endpoint (or which tab's Sign &
  // Send) produced the row — sort is stable, so same-timestamp rows keep the
  // order they were loaded in.
  const combined = useMemo(
    () =>
      [
        ...activeOrders.map((o) => ({
          ...o,
          _category: "Active",
          date: o.ordered,
        })),
        ...previousOrders.map((o) => ({
          ...o,
          _category: "Previous",
          ordered: o.date,
        })),
      ].sort((a, b) => orderedAtMs(b.ordered) - orderedAtMs(a.ordered)),
    [activeOrders, previousOrders],
  );

  const filtered = useMemo(
    () =>
      combined.filter((o) => {
        const matchesFilter = filter === "All" || o._category === filter;
        const q = search.toLowerCase();
        const matchesSearch =
          !q ||
          o.service.toLowerCase().includes(q) ||
          (o.orderNo || "").toLowerCase().includes(q);
        return matchesFilter && matchesSearch;
      }),
    [combined, filter, search],
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}
        >
          Orders ·{" "}
          <Typography
            component="span"
            sx={{ color: "#6B7280", fontWeight: 400 }}
          >
            {filtered.length} of {combined.length}
          </Typography>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            {ORDERS_FILTER_OPTIONS.map((opt) => (
              <Button
                key={opt}
                onClick={() => setFilter(opt)}
                sx={{
                  fontSize: "0.76rem",
                  textTransform: "none",
                  borderRadius: 0,
                  px: 1.5,
                  py: 0.5,
                  minWidth: 0,
                  fontWeight: filter === opt ? 700 : 400,
                  color: filter === opt ? "#fff" : "#374151",
                  backgroundColor: filter === opt ? "#0E3F66" : "#fff",
                  "&:hover": {
                    backgroundColor: filter === opt ? "#0a2e4d" : "#F9FAFB",
                  },
                }}
              >
                {opt}
              </Button>
            ))}
          </Box>
          <TextField
            size="small"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 220,
              "& .MuiInputBase-input": { fontSize: "0.82rem" },
            }}
          />
        </Box>
      </Box>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <ColHeader>Order No</ColHeader>
              <ColHeader>Type</ColHeader>
              <ColHeader width="23%">Service / Medicine</ColHeader>
              <ColHeader>Doctor</ColHeader>
              <ColHeader>Priority</ColHeader>
              <ColHeader>Date</ColHeader>
              <ColHeader>Status</ColHeader>
              <ColHeader>Department</ColHeader>
              <ColHeader>Report</ColHeader>
              <ColHeader>Action</ColHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: "#9CA3AF",
                    fontSize: "0.82rem",
                  }}
                >
                  No orders found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((o, index) => {
                const isPrevious = o._category === "Previous";
                const isPharmacy = o.type === "Pharmacy";
                const isRadiology = o.type === "Radiology";
                const canCancel =
                  !isPrevious &&
                  (isPharmacy
                    ? PHARMACY_CANCEL_STATUSES.includes(o.status)
                    : isRadiology
                      ? RADIOLOGY_CANCEL_STATUSES.includes(o.status)
                      : CANCEL_ALLOWED_STATUSES.includes(o.status));
                const canStop =
                  !isPrevious &&
                  isPharmacy &&
                  PHARMACY_STOP_STATUSES.includes(o.status);
                const cancelTooltip = isPharmacy
                  ? "Medicine already sent to nursing — use Stop to discontinue."
                  : "Already processed — cannot cancel.";
                // `o.id` is an order/sample identifier, not a row identifier: one
                // prescription order_no covers every medicine on it, and one lab
                // sample_id covers every test on it, so several rows legitimately
                // share an id (that is why dedupePharmacyRows keys on service, not
                // id). Index disambiguates them — without it React warned about
                // duplicate keys and could drop or duplicate rows.
                return (
                  <TableRow
                    key={`${o._category}-${o.id}-${index}`}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <BodyCell>
                      {o.orderNo ? (
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: "#111827",
                          }}
                        >
                          {o.orderNo}
                        </Typography>
                      ) : (
                        <Tooltip
                          title="This order reached us without an order number — nothing can act on it until the backend issues one."
                          placement="top"
                          arrow
                        >
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: "#9CA3AF",
                            }}
                          >
                            —
                          </Typography>
                        </Tooltip>
                      )}
                    </BodyCell>
                    <BodyCell>
                      <TypeBadge type={o.type} />
                    </BodyCell>
                    <BodyCell>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "#1D4ED8",
                        }}
                      >
                        {o.service}
                      </Typography>
                      {o.cpt && (
                        <Typography
                          sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}
                        >
                          {o.cpt}
                        </Typography>
                      )}
                    </BodyCell>
                    <BodyCell sx={{ whiteSpace: "nowrap" }}>
                      {o.doctor}
                    </BodyCell>
                    <BodyCell>
                      <PriorityChip priority={o.priority} />
                    </BodyCell>
                    <BodyCell
                      sx={{
                        whiteSpace: "nowrap",
                        color: "#6B7280",
                        fontSize: "0.75rem",
                      }}
                    >
                      {orderedLabel(o.ordered)}
                    </BodyCell>
                    {/* Read-only: this tab tracks where an order has got to, it
                      doesn't drive it. Status is moved by the department that
                      owns the work — lab/phlebotomy on /laboratories,
                      radiology on /radiology-services. */}
                    <BodyCell>
                      <StatusChip status={o.status} />
                    </BodyCell>
                    <BodyCell sx={{ color: "#1D4ED8", fontSize: "0.78rem" }}>
                      {o.dept || "—"}
                    </BodyCell>
                    {/* Read-only for the doctor: the report is published by the
                      lab / radiology console, this column only says whether it
                      is there yet. */}
                    <BodyCell>
                      {!REPORTABLE_TYPES.includes(o.type) ? (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#9CA3AF" }}
                        >
                          —
                        </Typography>
                      ) : isOrderReportReady(o) ? (
                        <Button
                          size="small"
                          startIcon={<AssignmentIcon sx={{ fontSize: 13 }} />}
                          onClick={() => onViewReport(o)}
                          sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            py: 0.2,
                            px: 1,
                            minWidth: 0,
                            color: "#0E3F66",
                            border: "1px solid #BFDBFE",
                            borderRadius: 1,
                            backgroundColor: "#EFF6FF",
                            "&:hover": { backgroundColor: "#DBEAFE" },
                          }}
                        >
                          View
                        </Button>
                      ) : (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#9CA3AF" }}
                        >
                          Not generated
                        </Typography>
                      )}
                    </BodyCell>
                    <BodyCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                          onClick={() => onViewClick(o)}
                          sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            py: 0.2,
                            px: 1,
                            minWidth: 0,
                            color: "#374151",
                            border: "1px solid #E5E7EB",
                            borderRadius: 1,
                          }}
                        >
                          View
                        </Button>
                        {!isPrevious &&
                          (canCancel ? (
                            <Button
                              size="small"
                              startIcon={
                                <CancelOutlinedIcon sx={{ fontSize: 13 }} />
                              }
                              onClick={() => onCancelClick(o)}
                              sx={{
                                fontSize: "0.72rem",
                                textTransform: "none",
                                py: 0.2,
                                px: 1,
                                minWidth: 0,
                                color: "#EF4444",
                                border: "1px solid #FECACA",
                                borderRadius: 1,
                              }}
                            >
                              Cancel
                            </Button>
                          ) : (
                            <Tooltip
                              title={cancelTooltip}
                              placement="top"
                              arrow
                            >
                              <span>
                                <Button
                                  size="small"
                                  disabled
                                  startIcon={
                                    <CancelOutlinedIcon sx={{ fontSize: 13 }} />
                                  }
                                  sx={{
                                    fontSize: "0.72rem",
                                    textTransform: "none",
                                    py: 0.2,
                                    px: 1,
                                    minWidth: 0,
                                    borderRadius: 1,
                                  }}
                                >
                                  Cancel
                                </Button>
                              </span>
                            </Tooltip>
                          ))}
                        {!isPrevious &&
                          isPharmacy &&
                          (canStop ? (
                            <Button
                              size="small"
                              startIcon={
                                <StopCircleOutlinedIcon sx={{ fontSize: 13 }} />
                              }
                              onClick={() => onStopClick(o)}
                              sx={{
                                fontSize: "0.72rem",
                                textTransform: "none",
                                py: 0.2,
                                px: 1,
                                minWidth: 0,
                                color: "#B45309",
                                border: "1px solid #FDE68A",
                                borderRadius: 1,
                                backgroundColor: "#FFFBEB",
                                "&:hover": { backgroundColor: "#FEF3C7" },
                              }}
                            >
                              Stop
                            </Button>
                          ) : (
                            <Tooltip
                              title="Medicine hasn't been dispensed yet — nothing to stop."
                              placement="top"
                              arrow
                            >
                              <span>
                                <Button
                                  size="small"
                                  disabled
                                  startIcon={
                                    <StopCircleOutlinedIcon
                                      sx={{ fontSize: 13 }}
                                    />
                                  }
                                  sx={{
                                    fontSize: "0.72rem",
                                    textTransform: "none",
                                    py: 0.2,
                                    px: 1,
                                    minWidth: 0,
                                    borderRadius: 1,
                                  }}
                                >
                                  Stop
                                </Button>
                              </span>
                            </Tooltip>
                          ))}
                      </Box>
                    </BodyCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Investigations Tab ───────────────────────────────────────────────────────

// [INVESTIGATIONS_API] Expanded per-item edit form inside the cart panel — mirrors
// PrescriptionCartItemFields' layout/spacing so the Investigations tab reads as the
// same structure as Pharmacy, just with test_items fields instead of medication ones.
const InvestigationCartItemFields = ({
  item,
  doctorName,
  onUpdate,
  investigationSchema,
}) => {
  const instructionOptions = investigationSchema?.instructionOptions?.length
    ? investigationSchema.instructionOptions
    : INSTRUCTION_OPTIONS;

  return (
    <>
      {item.category && (
        <Typography sx={{ fontSize: "0.72rem", color: "#6B7280", mb: 2 }}>
          {item.category}
        </Typography>
      )}
      {item.instructions && (
        <Box
          sx={{
            mb: 2,
            px: 1,
            py: 0.5,
            backgroundColor: "#FFFBEB",
            borderRadius: 1,
            border: "1px solid #FDE68A",
          }}
        >
          <Typography sx={{ fontSize: "0.7rem", color: "#92400E" }}>
            📋 Patient Instructions: {item.instructions}
          </Typography>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        label="Test Description"
        placeholder="Test Description (optional)"
        value={item.testDescription || ""}
        onChange={(e) => onUpdate(item.id, "testDescription", e.target.value)}
        sx={{ mb: 2, ...cartFieldSx }}
      />

      <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="CPT"
          value={item.cpt || "—"}
          InputProps={{ readOnly: true }}
          sx={{
            ...cartFieldSx,
            "& .MuiInputBase-input": {
              ...cartFieldSx["& .MuiInputBase-input"],
              backgroundColor: "#F9FAFB",
            },
          }}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Qty"
          value={item.qty}
          disabled={item.quantityModifiable === false}
          onChange={(e) =>
            onUpdate(item.id, "qty", Math.max(1, parseInt(e.target.value) || 1))
          }
          inputProps={{ min: 1 }}
          helperText={
            item.quantityModifiable === false ? "Qty locked by tariff" : " "
          }
          FormHelperTextProps={{
            sx: { fontSize: "0.65rem", m: 0, minHeight: 16 },
          }}
          sx={cartFieldSx}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Rate"
          value={item.rate ?? ""}
          disabled={item.chargeModifiable !== true}
          onChange={(e) =>
            onUpdate(
              item.id,
              "rate",
              e.target.value === "" ? "" : Number(e.target.value),
            )
          }
          InputProps={{
            readOnly: item.chargeModifiable !== true,
            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
          }}
          helperText={
            item.chargeModifiable !== true ? "Rate locked by tariff" : " "
          }
          FormHelperTextProps={{
            sx: { fontSize: "0.65rem", m: 0, minHeight: 16 },
          }}
          sx={{
            ...cartFieldSx,
            ...(item.chargeModifiable !== true
              ? {
                  "& .MuiInputBase-input": {
                    ...cartFieldSx["& .MuiInputBase-input"],
                    backgroundColor: "#F9FAFB",
                  },
                }
              : {}),
          }}
        />
      </Box>

      <TextField
        fullWidth
        size="small"
        type="datetime-local"
        label="Service Date"
        value={item.date}
        onChange={(e) => onUpdate(item.id, "date", e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2, ...cartFieldSx }}
      />

      <FormControl
        fullWidth
        size="small"
        required
        sx={{ mb: 2, ...cartFieldSx }}
      >
        <InputLabel required>Priority</InputLabel>
        <Select
          label="Priority"
          value={item.priority}
          onChange={(e) => onUpdate(item.id, "priority", e.target.value)}
          MenuProps={drawerSelectMenuProps}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <MenuItem key={p} value={p} sx={{ fontSize: "0.78rem" }}>
              {p}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small" sx={{ mb: 2, ...cartFieldSx }}>
        <InputLabel>Instruction</InputLabel>
        <Select
          label="Instruction"
          value={item.instruction || ""}
          onChange={(e) => onUpdate(item.id, "instruction", e.target.value)}
          MenuProps={drawerSelectMenuProps}
        >
          <MenuItem value="" sx={{ fontSize: "0.78rem", color: "#9CA3AF" }}>
            Select...
          </MenuItem>
          {instructionOptions.map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Checkbox
            size="small"
            checked={Boolean(item.emergency)}
            onChange={(e) => onUpdate(item.id, "emergency", e.target.checked)}
            sx={{ p: 0.4 }}
          />
          <Typography sx={{ fontSize: "0.78rem", color: "#374151" }}>
            Emergency
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Checkbox
            size="small"
            checked={Boolean(item.portable)}
            onChange={(e) => onUpdate(item.id, "portable", e.target.checked)}
            sx={{ p: 0.4 }}
          />
          <Typography sx={{ fontSize: "0.78rem", color: "#374151" }}>
            Portable
          </Typography>
        </Box>
      </Box>

      {item.interface && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography sx={{ fontSize: "0.78rem", color: "#6B7280" }}>
            Interface:
          </Typography>
          <InterfaceTag type={item.interface} />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography sx={{ fontSize: "0.78rem", color: "#6B7280" }}>
          Order By:
        </Typography>
        <Typography
          sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1F2937" }}
        >
          {doctorName || "—"}
        </Typography>
      </Box>

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Doctor Instructions"
        placeholder="Doctor Instructions…"
        value={item.doctorInstructions || ""}
        onChange={(e) =>
          onUpdate(item.id, "doctorInstructions", e.target.value)
        }
        sx={cartFieldSx}
      />
    </>
  );
};

// ─── Resizable cart panel ─────────────────────────────────────────────────────
// Same drag-to-resize behavior as the IPD doctor console's right panel: a 6px strip
// between the left list and the cart, width persisted per browser, double-click resets.
// Narrower than MIN and the cart's two-column dose/priority rows start wrapping badly;
// wider than MAX (or leaving the left list under its minimum) and the search results
// next to it stop being readable.
const CART_MIN_WIDTH = 360;
const CART_MAX_WIDTH = 900;
const CART_LEFT_MIN_WIDTH = 520;
const INVESTIGATION_CART_DEFAULT_WIDTH = 520;
const PHARMACY_CART_DEFAULT_WIDTH = 440;
const INVESTIGATION_CART_WIDTH_KEY = "ipdOrderMgmtInvestigationCartWidth";
const PHARMACY_CART_WIDTH_KEY = "ipdOrderMgmtPharmacyCartWidth";

// MAX is also capped by what's actually on screen — a 900px cart on a 1280px laptop
// would leave the search list unusable. Math.max at the end keeps MIN winning on narrow
// screens, where the viewport cap would otherwise drop below it.
const clampCartWidth = (px) => {
  const max = Math.max(
    CART_MIN_WIDTH,
    Math.min(CART_MAX_WIDTH, window.innerWidth - CART_LEFT_MIN_WIDTH),
  );
  return Math.min(Math.max(px, CART_MIN_WIDTH), max);
};

const useResizableCart = (defaultWidth, storageKey) => {
  const [width, setWidth] = useState(() => {
    try {
      const stored = Number(localStorage.getItem(storageKey));
      return stored ? clampCartWidth(stored) : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });

  // A stored width valid on a 1920 monitor can be far too wide once the same browser
  // opens on a laptop, so re-clamp whenever the viewport changes.
  useEffect(() => {
    const onWindowResize = () => setWidth((w) => clampCartWidth(w));
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  // The drag listens on window, not on the handle: a pointer moving faster than React
  // re-renders leaves a 6px strip immediately, and mouseup can land anywhere on the page.
  const startResize = (e) => {
    e.preventDefault();
    // The drawer is position:fixed over the whole viewport, so the cart is flush to the
    // right edge and its width is simply the distance from the cursor to that edge.
    const widthFromCursor = (ev) =>
      clampCartWidth(window.innerWidth - ev.clientX);

    const onMouseMove = (ev) => setWidth(widthFromCursor(ev));
    const onMouseUp = (ev) => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try {
        localStorage.setItem(storageKey, String(widthFromCursor(ev)));
      } catch {}
    };

    // Without these the drag selects text across the results list, and the cursor
    // flickers back to a caret every time it leaves the handle mid-drag.
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const resetWidth = () => {
    setWidth(defaultWidth);
    try {
      localStorage.setItem(storageKey, String(defaultWidth));
    } catch {}
  };

  return { width, startResize, resetWidth };
};

// Transparent until hovered so it doesn't read as a second border next to the cart's own.
const CartResizeHandle = ({ onMouseDown, onDoubleClick }) => (
  <Box
    onMouseDown={onMouseDown}
    onDoubleClick={onDoubleClick}
    title="Drag to resize · double-click to reset"
    sx={{
      width: 6,
      flexShrink: 0,
      cursor: "col-resize",
      backgroundColor: "transparent",
      transition: "background-color 0.15s",
      "&:hover": { backgroundColor: "#21cdc0" },
      "&:active": { backgroundColor: "#21cdc0" },
    }}
  />
);

// [INVESTIGATIONS_API] Right-hand cart panel — same collapsible-card structure as
// PrescriptionCartPanel (Pharmacy tab), just holding investigations instead of medicines.
const InvestigationCartPanel = ({
  pendingItems,
  doctorName,
  onRemove,
  onUpdate,
  investigationSchema,
  onSignAndSend,
  isSigning,
  width,
}) => {
  const [expandedIds, setExpandedIds] = useState(() =>
    pendingItems.length
      ? new Set([pendingItems[pendingItems.length - 1].id])
      : new Set(),
  );
  const itemRefs = useRef({});
  const prevLenRef = useRef(pendingItems.length);

  // Newly added investigations auto-expand and scroll into view; others collapse —
  // same behavior as the Pharmacy cart.
  useEffect(() => {
    const len = pendingItems.length;
    const idSet = new Set(pendingItems.map((p) => p.id));

    if (len === 0) {
      setExpandedIds(new Set());
    } else if (len > prevLenRef.current) {
      const newest = pendingItems[len - 1];
      setExpandedIds(new Set([newest.id]));
      window.requestAnimationFrame(() => {
        itemRefs.current[newest.id]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    } else {
      setExpandedIds((prev) => {
        const next = new Set([...prev].filter((id) => idSet.has(id)));
        if (next.size === 0 && len > 0) next.add(pendingItems[len - 1].id);
        return next;
      });
    }
    prevLenRef.current = len;
  }, [pendingItems]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        borderLeft: "1px solid #E5E7EB",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F3F4F6",
        p: 2,
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          ...cartBoxSx,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {/* Header — Priority/Service Date are edited per-item below, so no duplicate defaults here */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #E5E7EB",
            backgroundColor: "#fff",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.88rem",
              color: "#111827",
              whiteSpace: "nowrap",
            }}
          >
            New Investigation / Service Request · {pendingItems.length}
          </Typography>
        </Box>

        {/* Cart items — scrollable stack of collapsible investigation cards */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 1.5,
            backgroundColor: "#FAFAFA",
            minHeight: 0,
          }}
        >
          {pendingItems.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "#9CA3AF" }}>
              <ScienceIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
              <Typography sx={{ fontSize: "0.78rem" }}>
                No investigations added yet.
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", mt: 0.5, color: "#D1D5DB" }}
              >
                Add investigations from the list on the left.
              </Typography>
            </Box>
          ) : (
            pendingItems.map((item, index) => {
              const expanded = expandedIds.has(item.id);
              return (
                <Box
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[item.id] = el;
                  }}
                  sx={{
                    ...cartBoxSx,
                    mb: 1.5,
                    boxShadow: expanded
                      ? "0 2px 6px rgba(0,0,0,0.06)"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                    borderColor: expanded ? "#BFDBFE" : "#E5E7EB",
                  }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleExpanded(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExpanded(item.id);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      p: 1.5,
                      cursor: "pointer",
                      backgroundColor: expanded ? "#F0F9FF" : "#fff",
                      borderBottom: expanded ? "1px solid #E5E7EB" : "none",
                      "&:hover": {
                        backgroundColor: expanded ? "#F0F9FF" : "#F9FAFB",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.8,
                          mb: 0.3,
                        }}
                      >
                        <Chip
                          label={`${index + 1}/${pendingItems.length}`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            backgroundColor: "#EFF6FF",
                            color: "#0E3F66",
                          }}
                        />
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            color: "#1F2937",
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Box>
                      {!expanded && (
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            color: "#6B7280",
                            lineHeight: 1.4,
                          }}
                        >
                          {[
                            item.category,
                            item.priority,
                            item.emergency && "Emergency",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          {item.qty ? ` · Qty ${item.qty}` : ""}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(item.id);
                        }}
                        sx={{ color: "#6B7280", p: 0.3 }}
                      >
                        {expanded ? (
                          <ExpandLessIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(item.id);
                        }}
                        sx={{ color: "#EF4444", p: 0.3 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {expanded && (
                    <Box sx={{ p: 1.5, pt: 1.2 }}>
                      <InvestigationCartItemFields
                        item={item}
                        doctorName={doctorName}
                        onUpdate={onUpdate}
                        investigationSchema={investigationSchema}
                      />
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer actions inside outer box */}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#fff",
            display: "flex",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="small"
            disabled={pendingItems.length === 0 || isSigning}
            onClick={onSignAndSend}
            sx={{
              fontSize: "0.78rem",
              textTransform: "none",
              backgroundColor: "#0E3F66",
              borderRadius: "8px",
              py: 0.8,
              "&:hover": { backgroundColor: "#0a2e4d" },
            }}
          >
            {isSigning ? "Sending…" : "Sign & Send to Departments"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const InvestigationsTab = ({
  patient,
  activeOrders,
  onOrdersChanged,
  onLogToTimeline,
}) => {
  const [search, setSearch] = useState("");
  const [serviceType, setServiceType] = useState("All Service Types");
  const [department, setDepartment] = useState("Laboratory");
  const [pendingItems, setPendingItems] = useState([]);
  const [isSigningInvestigations, setIsSigningInvestigations] = useState(false);
  const admissionId = patient?.admissionId || patient?.id;
  const careContextId =
    patient?.careContextRef || patient?.care_context_ref || "";
  const cart = useResizableCart(
    INVESTIGATION_CART_DEFAULT_WIDTH,
    INVESTIGATION_CART_WIDTH_KEY,
  );

  // [INVESTIGATIONS_API] GET ipd/billing/charge-capture/services/ — patient-aware
  // (admission_id) + group_code for department (Radiology → RADIOLOGY, etc.).
  const [searchResults, setSearchResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState("idle"); // idle | loading | loaded | error
  const [searchError, setSearchError] = useState("");
  const investigationSearchSeqRef = useRef(0);

  // [INVESTIGATIONS_API] GET care_plan_structures/ — same endpoint/pattern the Pharmacy tab
  // uses for its medication schema, here sourcing the Test activity's test_items row fields
  // (test_description, emergency, instruction options) for the pending-items table below.
  const [investigationSchema, setInvestigationSchema] = useState(null);
  const investigationSchemaRef = useRef(null);
  const investigationStructuresFetchRef = useRef(null);

  const loadInvestigationSchema = useCallback(async () => {
    if (investigationSchemaRef.current) return investigationSchemaRef.current;
    if (investigationStructuresFetchRef.current)
      return investigationStructuresFetchRef.current;

    investigationStructuresFetchRef.current = axios
      .get(`${API_BASE}care_plan_structures/`, { headers: apiHeaders() })
      .then((response) => {
        const structures = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const schema = extractTestItemSchema(structures);
        investigationSchemaRef.current = schema;
        setInvestigationSchema(schema);
        return schema;
      })
      .catch((err) => {
        console.error("Failed to load care plan structures:", err);
        return null;
      })
      .finally(() => {
        investigationStructuresFetchRef.current = null;
      });

    return investigationStructuresFetchRef.current;
  }, []);

  useEffect(() => {
    loadInvestigationSchema();
  }, [loadInvestigationSchema]);

  const doctorName =
    patient?.doctor_name || patient?.consultant || patient?.doctorName || "";

  const runInvestigationSearch = useCallback(
    (query, dept) => {
      const seq = ++investigationSearchSeqRef.current;
      if (!query.trim()) {
        setSearchResults([]);
        setSearchStatus("idle");
        setSearchError("");
        return;
      }
      if (!admissionId) {
        setSearchResults([]);
        setSearchStatus("error");
        setSearchError(
          "Missing admission ID — cannot load patient-aware tariffs.",
        );
        return;
      }
      setSearchStatus("loading");
      setSearchError("");
      const groupCode = investigationCategoryToGroupCode(dept);
      searchChargeCaptureServices(query, {
        limit: 50,
        admissionId,
        groupCode,
      })
        .then((raw) => {
          if (seq !== investigationSearchSeqRef.current) return;
          const seenNames = new Set();
          const mapped = raw
            .map(mapMedicalServiceToInvestigationCard)
            .filter(Boolean)
            .sort((a, b) => {
              if (a._isHighQuality !== b._isHighQuality)
                return a._isHighQuality ? -1 : 1;
              return b._matchScore - a._matchScore;
            })
            .filter((item) => {
              const key = item.name.trim().toLowerCase();
              if (seenNames.has(key)) return false;
              seenNames.add(key);
              return true;
            });
          setSearchResults(mapped);
          setSearchStatus("loaded");
        })
        .catch((err) => {
          if (seq !== investigationSearchSeqRef.current) return;
          setSearchError(
            err.response?.data?.message ||
              err.message ||
              "Could not load investigations.",
          );
          setSearchStatus("error");
        });
    },
    [admissionId],
  );

  // Debounce typed search (~300ms) — re-runs on department change too.
  useEffect(() => {
    const handle = window.setTimeout(
      () => runInvestigationSearch(search, department),
      ITEM_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [search, department, runInvestigationSearch]);

  const displayResults = useMemo(() => {
    if (serviceType === "All Service Types") return searchResults;
    return searchResults.filter((s) => s.serviceType === serviceType);
  }, [searchResults, serviceType]);

  const toggleItem = (item) => {
    setPendingItems((prev) => {
      if (prev.find((p) => p.id === item.id))
        return prev.filter((p) => p.id !== item.id);
      return [
        ...prev,
        {
          ...item,
          qty: 1,
          date: todayDateTimeLocal(),
          priority: "Routine",
          portable: false,
          doctorInstructions: "",
          // [INVESTIGATIONS_API] Sibling test_items row fields from care_plan_structures/ —
          // test_name itself was already resolved via search, so it's not re-asked here.
          testDescription: "",
          emergency: false,
          instruction: "",
        },
      ];
    });
    loadInvestigationSchema();
  };

  const isSelected = (item) => pendingItems.some((p) => p.id === item.id);

  const updateItem = (id, field, value) =>
    setPendingItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    );

  // [INVESTIGATIONS_API] POST add_patient_lab_tests/ — same call Care Plan → Orders →
  // Investigation makes (see CarePlanActivityModal's postPatientLabTests), fired here too
  // so tests ordered from this drawer also land on the lab dashboard. Refreshes the Orders
  // tab's Lab/Radiology rows afterward via onOrdersChanged (get_ipd_lab_orders_by_admission/).
  const handleSignAndSendInvestigations = useCallback(async () => {
    if (pendingItems.length === 0) return;
    if (!admissionId) {
      toast.error("Cannot send investigation — missing admission ID.");
      return;
    }
    if (!careContextId) {
      toast.error("Cannot send investigation — missing care context.");
      return;
    }
    setIsSigningInvestigations(true);
    try {
      // One Sign & Send → one lab batch. A later Sign & Send for the same
      // patient gets a new id and shows as its own row on the lab dashboard.
      const labTestBatchId = createLabOrderBatchId();
      // Priority is per test[] entry because this endpoint creates one order per entry and
      // this tab lets the doctor set a different priority on each row.
      const testPayload = pendingItems.map((item) => ({
        name: item.name,
        code: item.code || "",
        medical_service_id: String(item.medical_service_id || item.id || ""),
        priority: toWirePriority(item.priority),
        lab_test_batch_id: labTestBatchId,
      }));
      const res = await axios.post(
        `${API_BASE}add_patient_lab_tests/`,
        {
          care_context_id: careContextId,
          admission_id: String(admissionId),
          patient_type: "IPD",
          status: "NEW",
          doctor_name: doctorName,
          lab_test_batch_id: labTestBatchId,
          // Order-level, alongside status — a priority sent only inside test[] came back
          // empty on the lab dashboard. This tab does let each row carry its own priority,
          // which one order-wide value can't express: if the rows disagree, the first row's
          // priority is what the backend records for the batch.
          priority: testPayload[0]?.priority || "Routine",
          test: testPayload,
        },
        { headers: apiHeaders() },
      );
      if (
        res.data?.is_valid === false ||
        String(res.data?.status || "").toLowerCase() === "failed"
      ) {
        throw new Error(
          res.data?.message || "Lab order was rejected by the server.",
        );
      }
      rememberLabOrderBatch(admissionId, labTestBatchId, testPayload);
      const nonLab = pendingItems.filter((item) => {
        const t = String(
          item.serviceType || item.categoryChip || "",
        ).toUpperCase();
        return (
          t && t !== "LABORATORY" && t !== "RADIOLOGY" && !t.includes("LAB")
        );
      });
      if (nonLab.length > 0) {
        toast.warn(
          "Some selected services are not Laboratory/Radiology — they may not appear on the lab dashboard.",
        );
      }
      toast.success("Investigation sent to lab.");
      // [TIMELINE] One test_order group per Sign & Send, its test_items mirroring the batch
      // that was just placed — the same shape Care Plan → Orders → Investigation saves.
      await onLogToTimeline?.({
        testOrders: [
          {
            category: department,
            priority: testPayload[0]?.priority || "Routine",
            test_items: pendingItems.map((item) => ({
              test_name: item.name,
              code: item.code || "",
              medical_service_id: String(
                item.medical_service_id || item.id || "",
              ),
            })),
          },
        ],
      });
      setPendingItems([]);
      onOrdersChanged?.();
    } catch (err) {
      console.error(
        "Failed to send investigation:",
        err.response?.data || err.message,
      );
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Could not send investigation. Please try again.",
      );
    } finally {
      setIsSigningInvestigations(false);
    }
  }, [
    pendingItems,
    admissionId,
    careContextId,
    doctorName,
    department,
    onOrdersChanged,
    onLogToTimeline,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Sub-tab bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          borderBottom: "1px solid #E5E7EB",
          pb: 0,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex" }}>
          <Button
            disableRipple
            sx={{
              fontSize: "0.82rem",
              textTransform: "none",
              fontWeight: 700,
              color: "#0E3F66",
              borderBottom: "2px solid #0E3F66",
              borderRadius: 0,
              px: 2,
              py: 1.2,
              mb: "-1px",
              cursor: "default",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Power Search
          </Button>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, pb: 1 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: "#22C55E",
            }}
          />
          <Typography sx={{ fontSize: "0.72rem", color: "#6B7280" }}>
            LIMS / RIS interface online
          </Typography>
        </Box>
      </Box>

      {/* ── Same left-list / right-cart structure as the Pharmacy tab ── */}
      <Box
        sx={{
          display: "flex",
          gap: 0,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Left — Power Search results */}
        <Box
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            maxWidth: `calc(100% - ${cart.width + 6}px)`,
            overflowY: "auto",
            p: 2,
            pr: 1.5,
          }}
        >
          {/* Search + filters */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Power search: labs, radiology, procedures, CPT code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiInputBase-input": { fontSize: "0.85rem" } }}
            />
            <Select
              size="small"
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              MenuProps={drawerSelectMenuProps}
              sx={{ fontSize: "0.82rem", minWidth: 170 }}
            >
              {SERVICE_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o} value={o} sx={{ fontSize: "0.82rem" }}>
                  {o}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              MenuProps={drawerSelectMenuProps}
              sx={{ fontSize: "0.82rem", minWidth: 160 }}
            >
              {DEPARTMENT_OPTIONS.map((o) => (
                <MenuItem key={o} value={o} sx={{ fontSize: "0.82rem" }}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Service cards */}
          {searchStatus === "loading" ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress size={28} sx={{ color: "#21cdc0", mb: 1.5 }} />
              <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                Searching investigations…
              </Typography>
            </Box>
          ) : searchStatus === "error" ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <ScienceIcon sx={{ fontSize: 52, color: "#E5E7EB", mb: 1.5 }} />
              <Typography sx={{ color: "#EF4444", fontSize: "0.85rem", mb: 1 }}>
                {searchError}
              </Typography>
              <Button
                size="small"
                onClick={() => runInvestigationSearch(search, department)}
                sx={{
                  fontSize: "0.78rem",
                  textTransform: "none",
                  color: "#21cdc0",
                }}
              >
                Retry
              </Button>
            </Box>
          ) : searchStatus === "idle" ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <ScienceIcon sx={{ fontSize: 52, color: "#E5E7EB", mb: 1.5 }} />
              <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                Start typing to search labs, radiology, procedures, or CPT code.
              </Typography>
            </Box>
          ) : displayResults.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
                mb: 2,
              }}
            >
              {displayResults.map((item) => {
                const selected = isSelected(item);
                return (
                  <Paper
                    key={item.id}
                    variant="outlined"
                    onClick={() => toggleItem(item)}
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      border: selected
                        ? "2px solid #21cdc0"
                        : "1px solid #E5E7EB",
                      backgroundColor: selected ? "#f0fffe" : "#fff",
                      transition: "border-color 0.15s, background-color 0.15s",
                      "&:hover": {
                        borderColor: "#21cdc0",
                        backgroundColor: "#f5fffe",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 0.3,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          color: "#1D4ED8",
                          flex: 1,
                          pr: 1,
                          lineHeight: 1.2,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          flexShrink: 0,
                          backgroundColor: selected ? "#21cdc0" : "transparent",
                          border: `2px solid ${selected ? "#21cdc0" : "#D1D5DB"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {selected ? (
                          <CheckCircleOutlineIcon
                            sx={{ fontSize: 13, color: "#fff" }}
                          />
                        ) : (
                          <AddIcon sx={{ fontSize: 12, color: "#9CA3AF" }} />
                        )}
                      </Box>
                    </Box>
                    <Typography
                      sx={{ fontSize: "0.66rem", color: "#6B7280", mb: 0.5 }}
                    >
                      {[
                        item.category,
                        item.serviceType,
                        item.cpt && `CPT ${item.cpt}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
                      {item.tat && <TATTag label={`TAT ${item.tat}`} />}
                      <InterfaceTag type={item.interface} />
                      {item.instructions && (
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 0.8,
                            py: "1px",
                            borderRadius: "4px",
                            backgroundColor: "#FFFBEB",
                            border: "1px solid #FDE68A",
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "0.65rem", color: "#92400E" }}
                          >
                            📋 Instructions
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <ScienceIcon sx={{ fontSize: 52, color: "#E5E7EB", mb: 1.5 }} />
              <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                No services match your filters.
              </Typography>
            </Box>
          )}
        </Box>

        <CartResizeHandle
          onMouseDown={cart.startResize}
          onDoubleClick={cart.resetWidth}
        />

        {/* Right — investigation cart (add then edit, same structure as Pharmacy) */}
        <InvestigationCartPanel
          pendingItems={pendingItems}
          doctorName={doctorName}
          onRemove={(id) =>
            setPendingItems((prev) => prev.filter((p) => p.id !== id))
          }
          onUpdate={updateItem}
          investigationSchema={investigationSchema}
          onSignAndSend={handleSignAndSendInvestigations}
          isSigning={isSigningInvestigations}
          width={cart.width}
        />
      </Box>
    </Box>
  );
};

// ─── Pharmacy Tab ─────────────────────────────────────────────────────────────

const MedicineFlag = ({ label }) => {
  const s = FLAG_STYLES[label] || {
    bg: "#F3F4F6",
    color: "#374151",
    border: "#E5E7EB",
  };
  return (
    <Box
      sx={{
        display: "inline-block",
        px: 0.7,
        py: "1px",
        borderRadius: "4px",
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        mr: 0.4,
        mb: 0.3,
      }}
    >
      <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: s.color }}>
        {label}
      </Typography>
    </Box>
  );
};

const formatDoseSummary = (item) => {
  const doses = DOSE_FIELDS.map(({ key, label }) => {
    const v = parseFloat(item[key]) || 0;
    return v > 0 ? `${label}:${v}` : null;
  }).filter(Boolean);
  return doses.length ? doses.join(" ") : "—";
};

const PrescriptionCartItemFields = ({
  item,
  totalQty,
  onUpdate,
  medicationSchema,
}) => {
  const routeOptions =
    getMedFieldOptions(medicationSchema, "route") || ROUTE_OPTIONS;
  const frequencyOptions =
    getMedFieldOptions(medicationSchema, "frequency") ||
    PRESCRIPTION_FREQUENCY_OPTIONS;
  const durationUnitOptions =
    getMedFieldOptions(medicationSchema, "duration_unit") ||
    DURATION_UNIT_OPTIONS;
  const instructionLangOptions =
    getMedFieldOptions(medicationSchema, "instruction_language") ||
    INSTRUCTION_LANGUAGE_OPTIONS;
  const priorityOptions = medicationSchema?.priorityOptions?.length
    ? medicationSchema.priorityOptions
    : ["routine", "urgent", "asap", "stat"];

  return (
    <>
      <FormControl
        fullWidth
        size="small"
        required
        sx={{ mb: 1.2, ...cartFieldSx }}
      >
        <InputLabel required>Priority</InputLabel>
        <Select
          label="Priority"
          value={item.priority || ""}
          onChange={(e) => onUpdate(item.id, "priority", e.target.value)}
          MenuProps={drawerSelectMenuProps}
        >
          <MenuItem value="" sx={{ display: "none" }} />
          {priorityOptions.map((p) => (
            <MenuItem key={p} value={p} sx={{ fontSize: "0.78rem" }}>
              {p}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        size="small"
        label="Reason"
        placeholder="Reason (optional)"
        value={item.reason || ""}
        onChange={(e) => onUpdate(item.id, "reason", e.target.value)}
        sx={{ mb: 1.2, ...cartFieldSx }}
      />

      <FormControl
        fullWidth
        size="small"
        required
        sx={{ mb: 1.2, ...cartFieldSx }}
      >
        <InputLabel required>Route</InputLabel>
        <Select
          label="Route"
          value={item.route || ""}
          onChange={(e) => onUpdate(item.id, "route", e.target.value)}
          MenuProps={drawerSelectMenuProps}
        >
          <MenuItem value="" sx={{ display: "none" }} />
          {routeOptions.map((r) => (
            <MenuItem key={r} value={r} sx={{ fontSize: "0.78rem" }}>
              {r}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        fullWidth
        size="small"
        required
        sx={{ mb: 1, ...cartFieldSx }}
      >
        <InputLabel required>Frequency</InputLabel>
        <Select
          label="Frequency"
          value={item.frequency || ""}
          onChange={(e) => onUpdate(item.id, "frequency", e.target.value)}
          MenuProps={drawerSelectMenuProps}
        >
          <MenuItem value="" sx={{ display: "none" }} />
          {frequencyOptions.map((f) => (
            <MenuItem key={f} value={f} sx={{ fontSize: "0.78rem" }}>
              {f}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 0.8,
          mb: 1.2,
        }}
      >
        {DOSE_FIELDS.map(({ key, label }) => (
          <Box
            key={key}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.3,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#6B7280",
                lineHeight: 1,
              }}
            >
              {label}
            </Typography>
            <TextField
              size="small"
              type="number"
              value={item[key]}
              onChange={(e) =>
                onUpdate(
                  item.id,
                  key,
                  e.target.value === ""
                    ? ""
                    : Math.max(0, parseFloat(e.target.value) || 0),
                )
              }
              inputProps={{
                min: 0,
                style: {
                  textAlign: "center",
                  fontSize: "0.78rem",
                  padding: "6px 2px",
                },
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 1.2 }}>
        <TextField
          fullWidth
          size="small"
          required
          type="number"
          label="Duration"
          value={item.duration}
          onChange={(e) =>
            onUpdate(
              item.id,
              "duration",
              e.target.value === ""
                ? ""
                : Math.max(0, parseFloat(e.target.value) || 0),
            )
          }
          inputProps={{ min: 0 }}
          sx={cartFieldSx}
        />
        <FormControl
          size="small"
          required
          sx={{ minWidth: 120, ...cartFieldSx }}
        >
          <InputLabel required>Unit</InputLabel>
          <Select
            label="Unit"
            value={item.durationUnit || ""}
            onChange={(e) => onUpdate(item.id, "durationUnit", e.target.value)}
            MenuProps={drawerSelectMenuProps}
          >
            <MenuItem value="" sx={{ display: "none" }} />
            {durationUnitOptions.map((u) => (
              <MenuItem key={u} value={u} sx={{ fontSize: "0.78rem" }}>
                {u}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 1.2 }}>
        <TextField
          fullWidth
          size="small"
          label="Total Quantity"
          value={totalQty}
          InputProps={{ readOnly: true }}
          sx={{
            ...cartFieldSx,
            "& .MuiInputBase-input": {
              ...cartFieldSx["& .MuiInputBase-input"],
              fontWeight: 700,
              backgroundColor: "#F9FAFB",
            },
          }}
        />
        {/* No displayEmpty/renderValue placeholder here: it repeated the label's
          own text underneath the unshrunk InputLabel, so the two stacked on top
          of each other in different type styles. Plain label, same as Route /
          Frequency / Unit above. */}
        <FormControl fullWidth size="small" sx={cartFieldSx}>
          <InputLabel>Instructions</InputLabel>
          <Select
            label="Instructions"
            value={item.instructions || ""}
            onChange={(e) => onUpdate(item.id, "instructions", e.target.value)}
            MenuProps={drawerSelectMenuProps}
          >
            <MenuItem value="" sx={{ fontSize: "0.78rem", color: "#9CA3AF" }}>
              Select...
            </MenuItem>
            {INSTRUCTION_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Remark"
        placeholder="Remark"
        value={item.remark || ""}
        onChange={(e) => onUpdate(item.id, "remark", e.target.value)}
        sx={{ mb: 1.2, ...cartFieldSx }}
      />

      {/* Same as Instructions above — label only, no duplicate placeholder. */}
      <FormControl fullWidth size="small" sx={{ mb: 1.2, ...cartFieldSx }}>
        <InputLabel>Instruction Language</InputLabel>
        <Select
          label="Instruction Language"
          value={item.instructionLanguage || ""}
          onChange={(e) =>
            onUpdate(item.id, "instructionLanguage", e.target.value)
          }
          MenuProps={drawerSelectMenuProps}
        >
          <MenuItem value="" sx={{ fontSize: "0.78rem", color: "#9CA3AF" }}>
            Select...
          </MenuItem>
          {instructionLangOptions.map((lang) => (
            <MenuItem key={lang} value={lang} sx={{ fontSize: "0.78rem" }}>
              {lang}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Prescription Advice"
        placeholder="Prescription Advice"
        value={item.prescriptionAdvice || ""}
        onChange={(e) =>
          onUpdate(item.id, "prescriptionAdvice", e.target.value)
        }
        sx={{ mb: 1.2, ...cartFieldSx }}
      />

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Patient Advice"
        placeholder="Patient Advice"
        value={item.patientAdvice || ""}
        onChange={(e) => onUpdate(item.id, "patientAdvice", e.target.value)}
        sx={{ mb: 1.2, ...cartFieldSx }}
      />

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Prescription Remark"
        placeholder="Prescription Remark"
        value={item.prescriptionRemark || ""}
        onChange={(e) =>
          onUpdate(item.id, "prescriptionRemark", e.target.value)
        }
        sx={cartFieldSx}
      />
    </>
  );
};

// Shared right-hand cart — used by both the Pharmacy tab and the Order Sets
// tab so items added from either place land in the same prescription, per
// the CPOE spec (one cart, one Sign & Send for the encounter).
const PrescriptionCartPanel = ({
  cartItems,
  pharmacy,
  setPharmacy,
  onRemove,
  onUpdate,
  medicationSchema,
  onSignAndSend,
  isSigning,
  width,
}) => {
  const [expandedIds, setExpandedIds] = useState(() =>
    cartItems.length
      ? new Set([cartItems[cartItems.length - 1].id])
      : new Set(),
  );
  const itemRefs = useRef({});
  const prevCartLenRef = useRef(cartItems.length);

  // Newly added medicines auto-expand and scroll into view; others collapse
  // so the doctor can configure one medicine at a time without losing context.
  useEffect(() => {
    const len = cartItems.length;
    const cartIdSet = new Set(cartItems.map((c) => c.id));

    if (len === 0) {
      setExpandedIds(new Set());
    } else if (len > prevCartLenRef.current) {
      const newest = cartItems[len - 1];
      setExpandedIds(new Set([newest.id]));
      window.requestAnimationFrame(() => {
        itemRefs.current[newest.id]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    } else {
      setExpandedIds((prev) => {
        const next = new Set([...prev].filter((id) => cartIdSet.has(id)));
        if (next.size === 0) next.add(cartItems[len - 1].id);
        return next;
      });
    }
    prevCartLenRef.current = len;
  }, [cartItems]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        borderLeft: "1px solid #E5E7EB",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F3F4F6",
        p: 2,
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          ...cartBoxSx,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {/* Header — title left, pharmacy dropdown right (same row) */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #E5E7EB",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.88rem",
              color: "#111827",
              whiteSpace: "nowrap",
            }}
          >
            Prescription Cart · {cartItems.length}
          </Typography>
        </Box>

        {/* Cart items — scrollable stack of collapsible medicine cards */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 1.5,
            backgroundColor: "#FAFAFA",
            minHeight: 0,
          }}
        >
          {cartItems.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "#9CA3AF" }}>
              <MedicationIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
              <Typography sx={{ fontSize: "0.78rem" }}>
                No medicines added yet.
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", mt: 0.5, color: "#D1D5DB" }}
              >
                Add medicines from the list on the left.
              </Typography>
            </Box>
          ) : (
            cartItems.map((item, index) => {
              const totalQty = calcTotalQuantity(item);
              const expanded = expandedIds.has(item.id);
              return (
                <Box
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[item.id] = el;
                  }}
                  sx={{
                    ...cartBoxSx,
                    mb: 1.5,
                    boxShadow: expanded
                      ? "0 2px 6px rgba(0,0,0,0.06)"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                    borderColor: expanded ? "#BFDBFE" : "#E5E7EB",
                  }}
                >
                  {/* Collapsed header — tap to expand/collapse */}
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleExpanded(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExpanded(item.id);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      p: 1.5,
                      cursor: "pointer",
                      backgroundColor: expanded ? "#F0F9FF" : "#fff",
                      borderBottom: expanded ? "1px solid #E5E7EB" : "none",
                      "&:hover": {
                        backgroundColor: expanded ? "#F0F9FF" : "#F9FAFB",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.8,
                          mb: 0.3,
                        }}
                      >
                        <Chip
                          label={`${index + 1}/${cartItems.length}`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            backgroundColor: "#EFF6FF",
                            color: "#0E3F66",
                          }}
                        />
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            color: "#1F2937",
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          color: "#9CA3AF",
                          mb: expanded ? 0 : 0.4,
                        }}
                      >
                        {item.generic}
                      </Typography>
                      {!expanded && (
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            color: "#6B7280",
                            lineHeight: 1.4,
                          }}
                        >
                          {[
                            item.route,
                            item.frequency,
                            formatDoseSummary(item) !== "—"
                              ? formatDoseSummary(item)
                              : "",
                            [item.duration, item.durationUnit]
                              .filter((v) => v !== "" && v != null)
                              .join(" "),
                            totalQty ? `Qty ${totalQty}` : "",
                          ]
                            .filter(Boolean)
                            .join(" · ") || "Fill prescription details"}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(item.id);
                        }}
                        sx={{ color: "#6B7280", p: 0.3 }}
                      >
                        {expanded ? (
                          <ExpandLessIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(item.id);
                        }}
                        sx={{ color: "#EF4444", p: 0.3 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {expanded && (
                    <Box sx={{ p: 1.5, pt: 1.2 }}>
                      <PrescriptionCartItemFields
                        item={item}
                        totalQty={totalQty}
                        onUpdate={onUpdate}
                        medicationSchema={medicationSchema}
                      />
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer actions inside outer box */}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#fff",
            display: "flex",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {/* <Button
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              fontSize: "0.78rem",
              textTransform: "none",
              borderColor: "#D1D5DB",
              color: "#374151",
              borderRadius: "8px",
              py: 0.8,
            }}
          >
            Save Draft
          </Button> */}
          <Button
            variant="contained"
            size="small"
            disabled={cartItems.length === 0 || isSigning}
            onClick={onSignAndSend}
            sx={{
              ml: "auto",
              fontSize: "0.78rem",
              textTransform: "none",
              backgroundColor: "#0E3F66",
              borderRadius: "8px",
              py: 0.8,
              px: 3,
              "&:hover": { backgroundColor: "#0a2e4d" },
            }}
          >
            {isSigning
              ? "Sending…"
              : `Sign & Send ${cartItems.length > 1 ? `(${cartItems.length})` : ""}`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const PharmacyTab = ({
  cartItems,
  pharmacy,
  setPharmacy,
  onAdd,
  onAddSetLineToCart,
  onRemove,
  onUpdate,
  isInCart,
  addingMedicineId,
  medicationSchema,
  onSignAndSend,
  isSigning,
}) => {
  // Two-step, the same shape as the Care Plan medication field:
  //   1. what is typed searches search_medicines/ for drug NAMES (no stock),
  //   2. picking one looks that name up in the pharmacy catalogue
  //      (inventory/pharmacy/prescribe/search/) for stock, flags and the
  //      inventory item_id a prescription actually needs.
  // Searching the catalogue directly on every keystroke — what this used to do —
  // skipped step 1 entirely and matched only what the store happens to carry.
  const [search, setSearch] = useState("");
  // Step 1 — search_medicines/ name suggestions.
  const [suggestions, setSuggestions] = useState([]);
  const [suggestStatus, setSuggestStatus] = useState("idle");
  const [suggestError, setSuggestError] = useState("");
  const suggestSeqRef = useRef(0);
  // Step 2 — the picked name and what the catalogue says about it.
  const [pickedMedicine, setPickedMedicine] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchError, setSearchError] = useState("");
  const searchSeqRef = useRef(0);
  const pickedSearchTermRef = useRef("");
  const cart = useResizableCart(
    PHARMACY_CART_DEFAULT_WIDTH,
    PHARMACY_CART_WIDTH_KEY,
  );

  // The pharmacy store both prescribe/search calls below are scoped to. Auto-
  // selected when the hospital has one; the picker above the search box appears
  // only when there's more than one.
  const pharmacyStore = usePharmacyStore();
  const pharmacyStoreId = pharmacyStore.storeId;

  // Order Set picker — same sets/expand data as the old standalone Order Sets
  // tab, now surfaced as a dropdown right under the medicine search box.
  const [orderSets, setOrderSets] = useState([]);
  const [orderSetsStatus, setOrderSetsStatus] = useState("loading");
  const [orderSetsError, setOrderSetsError] = useState("");
  const [selectedSetId, setSelectedSetId] = useState("");
  const [expandState, setExpandState] = useState({});
  const [confirmAdd, setConfirmAdd] = useState(null);
  // Substitute-by-generic-name — keyed by the out-of-stock medicine's id so
  // each row's "Find substitutes" toggle and results are independent.
  const [substitutesByMed, setSubstitutesByMed] = useState({});
  const expandCacheRef = useRef(new Map());

  useEffect(() => {
    setOrderSetsStatus("loading");
    setOrderSetsError("");
    listOrderSets()
      .then((data) => {
        setOrderSets(Array.isArray(data) ? data : []);
        setOrderSetsStatus("loaded");
      })
      .catch((err) => {
        setOrderSetsError(err.message);
        setOrderSetsStatus("error");
      });
  }, []);

  const selectedSet =
    orderSets.find((s) => String(getSetId(s)) === String(selectedSetId)) ||
    null;

  const fetchExpand = (set) => {
    const id = getSetId(set);
    if (expandCacheRef.current.has(id)) {
      const lines = expandCacheRef.current.get(id);
      setExpandState((prev) => ({
        ...prev,
        [id]: { open: true, status: "loaded", lines, error: "" },
      }));
      return;
    }
    setExpandState((prev) => ({
      ...prev,
      [id]: { open: true, status: "loading", error: "" },
    }));
    expandOrderSet(id)
      .then((lines) => {
        const safeLines = Array.isArray(lines) ? lines : [];
        expandCacheRef.current.set(id, safeLines);
        setExpandState((prev) => ({
          ...prev,
          [id]: { open: true, status: "loaded", lines: safeLines, error: "" },
        }));
      })
      .catch((err) => {
        setExpandState((prev) => ({
          ...prev,
          [id]: { open: true, status: "error", error: err.message },
        }));
      });
  };

  const handleSelectSet = (id) => {
    setSelectedSetId(id);
    if (!id) return;
    const set = orderSets.find((s) => String(getSetId(s)) === String(id));
    if (set) fetchExpand(set);
  };

  const handleAddOne = (line) => {
    if (line.warning) {
      setConfirmAdd({ lines: [line] });
      return;
    }
    onAddSetLineToCart(line);
  };

  const handleAddAll = (set, lines) => {
    const pending = lines.filter((l) => !isInCart(l.item_id));
    if (pending.length === 0) return;
    if (pending.some((l) => l.warning)) {
      setConfirmAdd({ lines: pending });
      return;
    }
    pending.forEach(onAddSetLineToCart);
  };

  const confirmAddNow = () => {
    if (confirmAdd) confirmAdd.lines.forEach(onAddSetLineToCart);
    setConfirmAdd(null);
  };

  // ── Step 1: search_medicines/ ─────────────────────────────────────────────
  // Deliberately not scoped to a pharmacy store: this is the drug dictionary, so a
  // doctor can find a name before deciding which counter dispenses it.
  const runSuggestSearch = useCallback((name) => {
    const seq = ++suggestSeqRef.current;
    setSuggestStatus("loading");
    setSuggestError("");
    searchMedicineTerms(name)
      .then((items) => {
        if (seq !== suggestSeqRef.current) return;
        setSuggestions(items);
        setSuggestStatus("loaded");
      })
      .catch((err) => {
        if (seq !== suggestSeqRef.current) return;
        setSuggestError(err.message || "Could not search medicines.");
        setSuggestStatus("error");
      });
  }, []);

  const clearSuggestions = useCallback(() => {
    suggestSeqRef.current += 1;
    setSuggestions([]);
    setSuggestStatus("idle");
    setSuggestError("");
  }, []);

  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed.length < MEDICINE_TYPEAHEAD_MIN_CHARS) {
      clearSuggestions();
      return undefined;
    }
    // Spinner covers the debounce too — otherwise the field looks dead for a full
    // second after typing before the request even starts.
    setSuggestStatus("loading");
    const handle = window.setTimeout(
      () => runSuggestSearch(trimmed),
      MEDICINE_TYPEAHEAD_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [search, runSuggestSearch, clearSuggestions]);

  // Clear the previously picked medicine if the user starts a new search with
  // a different search term. This lets them search for another medicine after
  // picking one.
  useEffect(() => {
    if (pickedMedicine && search.trim() !== pickedSearchTermRef.current) {
      searchSeqRef.current += 1;
      setPickedMedicine(null);
      setMedicines([]);
      setSubstitutesByMed({});
      setSearchStatus("idle");
      setSearchError("");
    }
  }, [search, pickedMedicine]);

  // ── Step 2: inventory/pharmacy/prescribe/search/ ──────────────────────────
  // Runs only once a name has been picked. q is the brand/molecule after
  // stripDoseForm, because search_medicines/ terms carry the dose and form
  // ("Calpol 650mg Tablet") which the catalogue does not index.
  const runStockLookup = useCallback(
    (picked) => {
      const seq = ++searchSeqRef.current;
      const brandQuery = stripDoseForm(picked?.name);
      // Substitute panels are keyed by medicine id, so without this a row that comes
      // back in a later lookup still shows the panel it was left expanded with.
      setSubstitutesByMed({});
      if (!brandQuery || !pharmacyStoreId) {
        setMedicines([]);
        setSearchStatus("idle");
        setSearchError("");
        return;
      }
      setSearchStatus("loading");
      setSearchError("");
      searchPharmacyItems({
        q: picked?.name || brandQuery,
        limit: 20,
        locationId: pharmacyStoreId,
      })
        .then(({ items }) => {
          if (seq !== searchSeqRef.current) return;
          const mapped = (items || [])
            .map(mapPharmacyItemToMedicine)
            .filter(Boolean);
          // If medicine not found in catalogue, add as off-catalogue entry
          if (mapped.length === 0 && picked) {
            mapped.push({
              id: `offcat-${picked.conceptId}`,
              name: picked.name,
              generic: picked.generic,
              stock: 0,
              has_stock: false,
            });
          }
          setMedicines(mapped);
          setSearchStatus("loaded");
        })
        .catch((err) => {
          if (seq !== searchSeqRef.current) return;
          setSearchError(err.message || "Could not load medicines.");
          setSearchStatus("error");
        });
    },
    [pharmacyStoreId],
  );

  const handlePickMedicine = (med) => {
    setPickedMedicine(med);
    pickedSearchTermRef.current = search.trim();
    setSuggestions([]);
    setSuggestStatus("idle");
    runStockLookup(med);
  };

  const handleClearPick = () => {
    searchSeqRef.current += 1;
    setPickedMedicine(null);
    setMedicines([]);
    setSubstitutesByMed({});
    setSearchStatus("idle");
    setSearchError("");
  };

  const retrySearch = () => {
    if (pickedMedicine) runStockLookup(pickedMedicine);
  };

  // With several pharmacy stores the doctor usually picks the drug first and the
  // store second — that first lookup was skipped for want of a location_id, so
  // replay it once a store exists (same reason as the Care Plan replay effect).
  useEffect(() => {
    if (pickedMedicine) runStockLookup(pickedMedicine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyStoreId]);

  const toggleSubstitutes = (med) => {
    const key = med.id;
    setSubstitutesByMed((prev) => {
      const existing = prev[key];
      if (existing?.open)
        return { ...prev, [key]: { ...existing, open: false } };
      return { ...prev, [key]: { ...(existing || { items: [] }), open: true } };
    });
    const already = substitutesByMed[key];
    if (
      already?.items?.length ||
      already?.status === "loading" ||
      !med.generic ||
      med.generic === "—"
    )
      return;
    setSubstitutesByMed((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        open: true,
        status: "loading",
        items: [],
        error: "",
      },
    }));
    searchPharmacyItems({
      genericName: med.generic,
      availableOnly: true,
      limit: 10,
      locationId: pharmacyStoreId,
    })
      .then(({ items }) => {
        const mapped = (items || [])
          .map(mapPharmacyItemToMedicine)
          .filter(Boolean)
          .filter((m) => m.id !== med.id);
        setSubstitutesByMed((prev) => ({
          ...prev,
          [key]: { open: true, status: "loaded", items: mapped, error: "" },
        }));
      })
      .catch((err) => {
        setSubstitutesByMed((prev) => ({
          ...prev,
          [key]: {
            open: true,
            status: "error",
            items: [],
            error: err.message || "Could not load substitutes.",
          },
        }));
      });
  };

  const displayedMedicines = medicines;

  // A substitute renders as a sibling row of the SAME table as everything else —
  // a nested <Table> inside a colSpan cell (what this used to do) has its own
  // column widths and its own cell padding, so no substitute column ever lined
  // up with the header above it. The amber tint + indent is what marks the
  // nesting now; alignment comes for free from the shared <colgroup>.
  const renderMedicineRow = (
    med,
    { keyPrefix = "", isSubstitute = false } = {},
  ) => {
    const rowKey = `${keyPrefix}${med.id}`;
    // Check if medicine is in cart by name since API sources use different IDs
    const inCart = cartItems.some(
      (c) => c.name && med.name && c.name.trim().toLowerCase() === med.name.trim().toLowerCase()
    );
    const flags = getMedicineFlags(med);
    const isOOS = flags.includes("OOS");
    const subState = substitutesByMed[med.id];
    const rows = [
      <TableRow
        key={rowKey}
        hover
        sx={{
          "&:last-child td": { borderBottom: 0 },
          ...(isSubstitute && {
            backgroundColor: "#FFFBEB",
            "& td": { borderBottom: "1px solid #FDE68A" },
            "& td:first-of-type": { borderLeft: "3px solid #FCD34D" },
          }),
        }}
      >
        <BodyCell sx={isSubstitute ? { pl: 3.5 } : undefined}>
          <Typography
            sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1F2937" }}
          >
            {med.name}
          </Typography>
          <Typography sx={{ fontSize: "0.68rem", color: "#9CA3AF" }}>
            {med.form || "—"}
          </Typography>
        </BodyCell>
        <BodyCell sx={{ fontSize: "0.78rem", color: "#374151" }}>
          {med.generic}
        </BodyCell>
        <BodyCell sx={{ fontSize: "0.78rem", color: "#6B7280" }}>
          {med.brand}
        </BodyCell>
        <BodyCell>
          <Typography
            sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#374151" }}
          >
            {med.stock == null
              ? "—"
              : Number.isInteger(med.stock)
                ? med.stock
                : med.stock.toFixed(0)}
          </Typography>
        </BodyCell>
        <BodyCell>
          {flags.map((f) => (
            <MedicineFlag key={f} label={f} />
          ))}
        </BodyCell>
        <BodyCell>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.3,
            }}
          >
            {inCart ? (
              <Typography
                sx={{ fontSize: "0.72rem", color: "#21cdc0", fontWeight: 600 }}
              >
                Added
              </Typography>
            ) : (
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                onClick={() => onAdd(med)}
                disabled={addingMedicineId === med.id}
                sx={{
                  fontSize: "0.72rem",
                  textTransform: "none",
                  py: 0.2,
                  px: 1,
                  minWidth: 0,
                  color: "#0E3F66",
                  border: "1px solid #BFDBFE",
                  borderRadius: 1,
                  backgroundColor: "#EFF6FF",
                  "&:hover": { backgroundColor: "#DBEAFE" },
                }}
              >
                {addingMedicineId === med.id ? "Adding..." : "Add"}
              </Button>
            )}
            {isOOS && !isSubstitute && (
              <Button
                size="small"
                onClick={() => toggleSubstitutes(med)}
                sx={{
                  fontSize: "0.66rem",
                  textTransform: "none",
                  py: 0,
                  px: 0.3,
                  minWidth: 0,
                  color: "#B45309",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {subState?.open ? "Hide substitutes" : "Find substitutes"}
              </Button>
            )}
          </Box>
        </BodyCell>
      </TableRow>,
    ];

    if (isOOS && !isSubstitute && subState?.open) {
      const hasItems =
        subState.status === "loaded" && subState.items.length > 0;
      // Caption/status spans the full width — it's a banner, not tabular data,
      // so colSpan is correct here and costs no alignment.
      rows.push(
        <TableRow key={`${rowKey}-substitutes`}>
          <TableCell
            colSpan={6}
            sx={{
              py: 0.75,
              px: 1.5,
              pl: 3.5,
              backgroundColor: "#FFFBEB",
              borderBottom: hasItems ? "none" : "1px solid #FDE68A",
              borderLeft: "3px solid #FCD34D",
            }}
          >
            {subState.status === "loading" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={13} sx={{ color: "#0E3F66" }} />
                <Typography sx={{ fontSize: "0.74rem", color: "#6B7280" }}>
                  Searching substitutes for {med.generic}...
                </Typography>
              </Box>
            )}
            {subState.status === "error" && (
              <Typography sx={{ fontSize: "0.74rem", color: "#991B1B" }}>
                {subState.error}
              </Typography>
            )}
            {subState.status === "loaded" && subState.items.length === 0 && (
              <Typography sx={{ fontSize: "0.74rem", color: "#9CA3AF" }}>
                No in-stock substitutes found for {med.generic}.
              </Typography>
            )}
            {hasItems && (
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: "#B45309",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Substitutes for {med.generic}
              </Typography>
            )}
          </TableCell>
        </TableRow>,
      );

      if (hasItems) {
        subState.items.forEach((sub) => {
          rows.push(
            ...renderMedicineRow(sub, {
              keyPrefix: `${rowKey}-sub-`,
              isSubstitute: true,
            }),
          );
        });
      }
    }

    return rows;
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0,
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Left — medicine search → catalogue (prescribe/search) */}
      <Box
        sx={{
          flex: "1 1 0",
          minWidth: 0,
          maxWidth: `calc(100% - ${cart.width + 6}px)`,
          overflowY: "auto",
          p: 2,
          pr: 1.5,
        }}
      >
        {/* Store scope for the search below. Hidden entirely when the hospital
            has a single pharmacy store — the hook has already picked it. */}
        <PharmacyStoreSelect
          stores={pharmacyStore.stores}
          storeId={pharmacyStore.storeId}
          onChange={pharmacyStore.setStoreId}
          loading={pharmacyStore.loading}
          error={pharmacyStore.error}
          showPicker={pharmacyStore.showPicker}
          menuProps={drawerSelectMenuProps}
        />
        <TextField
          fullWidth
          size="small"
          disabled={!pharmacyStore.storeId}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={pharmacyStore.storeId ? "Search by generic name, brand, or molecule..." : "Select a pharmacy store first"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchStatus === "loading" && (
                  <CircularProgress
                    color="inherit"
                    size={16}
                    sx={{ mr: search ? 0.5 : 0 }}
                  />
                )}
                {!!search && (
                  <IconButton
                    size="small"
                    onClick={() => setSearch("")}
                    sx={{ p: 0.3 }}
                  >
                    <CloseIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            mt: pharmacyStore.showPicker || pharmacyStore.error ? 1.5 : 0,
            mb: 1.5,
            "& .MuiInputBase-input": { fontSize: "0.85rem" },
          }}
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Select
            displayEmpty
            value={selectedSetId}
            onChange={(e) => handleSelectSet(e.target.value)}
            MenuProps={drawerSelectMenuProps}
            renderValue={(v) => {
              if (!v)
                return (
                  <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                    Order Set — pick a saved set to add its items...
                  </Typography>
                );
              return (
                <Typography sx={{ fontSize: "0.85rem" }}>
                  {selectedSet
                    ? `${selectedSet.set_name} (${selectedSet.set_code})`
                    : v}
                </Typography>
              );
            }}
            sx={{ fontSize: "0.85rem" }}
          >
            <MenuItem value="" sx={{ fontSize: "0.85rem" }}>
              <em>None</em>
            </MenuItem>
            {orderSetsStatus === "loading" && (
              <MenuItem disabled sx={{ fontSize: "0.82rem" }}>
                Loading order sets...
              </MenuItem>
            )}
            {orderSetsStatus === "error" && (
              <MenuItem disabled sx={{ fontSize: "0.82rem", color: "#991B1B" }}>
                {orderSetsError || "Could not load order sets."}
              </MenuItem>
            )}
            {orderSetsStatus === "loaded" && orderSets.length === 0 && (
              <MenuItem disabled sx={{ fontSize: "0.82rem" }}>
                No order sets yet for this hospital.
              </MenuItem>
            )}
            {orderSets.map((set) => (
              <MenuItem
                key={getSetId(set)}
                value={getSetId(set)}
                sx={{ fontSize: "0.85rem" }}
              >
                {set.set_name}{" "}
                <Typography
                  component="span"
                  sx={{ ml: 0.8, fontSize: "0.72rem", color: "#9CA3AF" }}
                >
                  {set.set_code}
                  {set.specialty ? ` · ${set.specialty}` : ""}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedSet && (
          <Box sx={{ mb: 2 }}>
            <OrderSetRow
              set={selectedSet}
              state={expandState[getSetId(selectedSet)]}
              onToggleExpand={() => setSelectedSetId("")}
              onRetry={fetchExpand}
              onAddOne={handleAddOne}
              onAddAll={handleAddAll}
              isInCart={isInCart}
            />
          </Box>
        )}

        {/* Step 1 & 2 — search_medicines suggestions or picked medicine table. */}
        {(
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden" }}
          >
            {!pickedMedicine ? (
              // Show suggestions when no medicine is picked
              <>
                {suggestStatus === "idle" ? (
                  <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
                    <MedicationIcon
                      sx={{ fontSize: 40, color: "#E5E7EB", mb: 1 }}
                    />
                    <Typography sx={{ color: "#9CA3AF", fontSize: "0.82rem" }}>
                      Type at least {MEDICINE_TYPEAHEAD_MIN_CHARS} characters to
                      search medicines.
                    </Typography>
                  </Box>
                ) : suggestStatus === "loading" ? (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <CircularProgress size={22} sx={{ color: "#0E3F66", mb: 1 }} />
                    <Typography sx={{ color: "#9CA3AF", fontSize: "0.82rem" }}>
                      Searching medicines...
                    </Typography>
                  </Box>
                ) : suggestStatus === "error" ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography
                      sx={{ color: "#991B1B", fontSize: "0.82rem", mb: 1 }}
                    >
                      {suggestError}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => runSuggestSearch(search.trim())}
                      sx={{ textTransform: "none", fontSize: "0.78rem" }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : suggestions.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
                    <Typography sx={{ color: "#9CA3AF", fontSize: "0.82rem" }}>
                      No medicines found.
                    </Typography>
                  </Box>
                ) : (
                  suggestions.map((med, index) => (
                    <Box
                      key={`${med.conceptId || med.name}-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handlePickMedicine(med)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handlePickMedicine(med);
                        }
                      }}
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        cursor: "pointer",
                        borderBottom:
                          index === suggestions.length - 1
                            ? "none"
                            : "1px solid #F3F4F6",
                        "&:hover": { backgroundColor: "#F5FFFE" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: "#1F2937",
                        }}
                      >
                        {med.name}
                      </Typography>
                      {med.generic && (
                        <Typography sx={{ fontSize: "0.72rem", color: "#9CA3AF" }}>
                          {med.generic}
                        </Typography>
                      )}
                    </Box>
                  ))
                )}
              </>
            ) : (
              // Show table when medicine is picked
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <ColHeader width="28%">Medicine</ColHeader>
                      <ColHeader width="24%">Generic</ColHeader>
                      <ColHeader width="16%">Brand</ColHeader>
                      <ColHeader width="10%">Stock</ColHeader>
                      <ColHeader width="14%">Flags</ColHeader>
                      <ColHeader width="8%"></ColHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {searchStatus === "idle" ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          textAlign: "center",
                          py: 5,
                          color: "#9CA3AF",
                          fontSize: "0.82rem",
                        }}
                      >
                        {/* This table only renders once a name is picked, and the
                            lookup goes idle for exactly one reason: no store to
                            price stock against. */}
                        Select a pharmacy store to see stock for this medicine.
                      </TableCell>
                    </TableRow>
                  ) : searchStatus === "loading" && medicines.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ textAlign: "center", py: 5 }}
                      >
                        <CircularProgress
                          size={22}
                          sx={{ color: "#0E3F66", mb: 1 }}
                        />
                        <Typography
                          sx={{ color: "#9CA3AF", fontSize: "0.82rem" }}
                        >
                          Loading medicines...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : searchStatus === "error" ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography
                          sx={{ color: "#991B1B", fontSize: "0.82rem", mb: 1 }}
                        >
                          {searchError}
                        </Typography>
                        <Button
                          size="small"
                          onClick={retrySearch}
                          sx={{ textTransform: "none", fontSize: "0.78rem" }}
                        >
                          Retry
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : displayedMedicines.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          textAlign: "center",
                          py: 5,
                          color: "#9CA3AF",
                          fontSize: "0.82rem",
                        }}
                      >
                        No medicines found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedMedicines.map((med) => renderMedicineRow(med))
                  )}
                </TableBody>
              </Table>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      <CartResizeHandle
        onMouseDown={cart.startResize}
        onDoubleClick={cart.resetWidth}
      />

      {/* Right — prescription cart (wider, box-in-box) */}
      <PrescriptionCartPanel
        cartItems={cartItems}
        pharmacy={pharmacy}
        setPharmacy={setPharmacy}
        onRemove={onRemove}
        onUpdate={onUpdate}
        medicationSchema={medicationSchema}
        onSignAndSend={onSignAndSend}
        isSigning={isSigning}
        width={cart.width}
      />

      <LowStockConfirmDialog
        open={!!confirmAdd}
        count={confirmAdd?.lines?.length || 0}
        onCancel={() => setConfirmAdd(null)}
        onConfirm={confirmAddNow}
      />
    </Box>
  );
};

const getSetId = (s) => s?.id ?? s?.order_set_id ?? s?.set_id;
const itemCountFor = (s) =>
  s?.item_count ?? (Array.isArray(s?.items) ? s.items.length : 0);

const MonoBadge = ({ label }) => (
  <Box
    sx={{
      display: "inline-block",
      px: 0.8,
      py: "1px",
      borderRadius: "4px",
      backgroundColor: "#F3F4F6",
      border: "1px solid #E5E7EB",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.68rem",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontWeight: 600,
        color: "#374151",
      }}
    >
      {label}
    </Typography>
  </Box>
);

// Specialties are open-ended (derived from whatever sets the hospital has),
// so pills get a stable color from a hash rather than a fixed lookup table.
const SPECIALTY_PALETTE = [
  { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
  { bg: "#F0FDF4", color: "#15803D", border: "#BBF7D0" },
  { bg: "#FDF4FF", color: "#7E22CE", border: "#E9D5FF" },
  { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
  { bg: "#ECFEFF", color: "#0E7490", border: "#A5F3FC" },
  { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
];
const specialtyStyleFor = (label = "") => {
  let hash = 0;
  for (let i = 0; i < label.length; i++)
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  return SPECIALTY_PALETTE[hash % SPECIALTY_PALETTE.length];
};
const SpecialtyPill = ({ label }) => {
  const s = specialtyStyleFor(label);
  return (
    <Box
      sx={{
        display: "inline-block",
        px: 0.8,
        py: "1px",
        borderRadius: "4px",
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: s.color }}>
        {label}
      </Typography>
    </Box>
  );
};

const WarningPill = ({ label, severe }) => (
  <Tooltip title={label} placement="top" arrow>
    <Box
      sx={{
        display: "inline-block",
        maxWidth: 150,
        px: 0.7,
        py: "1px",
        borderRadius: "4px",
        overflow: "hidden",
        backgroundColor: severe ? "#FEF2F2" : "#FFF7ED",
        border: `1px solid ${severe ? "#FECACA" : "#FED7AA"}`,
      }}
    >
      <Typography
        noWrap
        sx={{
          fontSize: "0.65rem",
          fontWeight: 600,
          color: severe ? "#991B1B" : "#C2410C",
        }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

const LoadingBlock = ({ label }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      py: 5,
      color: "#6B7280",
    }}
  >
    <CircularProgress size={16} sx={{ color: "#0E3F66" }} />
    <Typography sx={{ fontSize: "0.82rem" }}>{label}</Typography>
  </Box>
);

const ErrorBlock = ({ message, onRetry }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      p: 1.5,
      my: 1,
      backgroundColor: "#FEF2F2",
      border: "1px solid #FECACA",
      borderRadius: 1.5,
    }}
  >
    <Typography sx={{ fontSize: "0.8rem", color: "#991B1B" }}>
      {message || "Something went wrong."}
    </Typography>
    <Button
      size="small"
      onClick={onRetry}
      sx={{
        fontSize: "0.76rem",
        fontWeight: 700,
        textTransform: "none",
        color: "#991B1B",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      Retry
    </Button>
  </Box>
);

const LowStockConfirmDialog = ({ open, count, onCancel, onConfirm }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="xs"
    fullWidth
    sx={{ zIndex: 1500 }}
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "#FFFBEB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.3,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 18, color: "#B45309" }} />
        </Box>
        <Box>
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}
          >
            Add despite low stock?
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", mt: 0.2 }}>
            {count === 1
              ? "This item has a stock warning."
              : `${count} items have stock warnings.`}
          </Typography>
        </Box>
      </Box>
    </DialogTitle>
    <DialogContent sx={{ pt: 1, px: 3 }}>
      <Typography
        sx={{ fontSize: "0.82rem", color: "#6B7280", lineHeight: 1.5 }}
      >
        Availability at the selected pharmacy is limited or unconfirmed. You can
        still add {count === 1 ? "it" : "them"} to the prescription cart and
        adjust before Sign & Send.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
      <Button
        onClick={onCancel}
        size="small"
        variant="outlined"
        sx={{
          textTransform: "none",
          fontSize: "0.83rem",
          borderColor: "#E5E7EB",
          color: "#374151",
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        size="small"
        variant="contained"
        sx={{
          textTransform: "none",
          fontSize: "0.83rem",
          backgroundColor: "#0E3F66",
          "&:hover": { backgroundColor: "#0a2e4d" },
        }}
      >
        Add Anyway
      </Button>
    </DialogActions>
  </Dialog>
);

const OrderSetRow = ({
  set,
  state,
  onToggleExpand,
  onRetry,
  onAddOne,
  onAddAll,
  isInCart,
}) => {
  const open = !!state?.open;
  const lines = state?.lines || [];
  const allAdded = lines.length > 0 && lines.every((l) => isInCart(l.item_id));
  const count = itemCountFor(set);

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, mb: 1.5, overflow: "hidden" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          p: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: 0.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.86rem", color: "#1F2937" }}
            >
              {set.set_name}
            </Typography>
            <MonoBadge label={set.set_code} />
            {getSetId(set) && <MonoBadge label={`ID ${getSetId(set)}`} />}
            {set.specialty && <SpecialtyPill label={set.specialty} />}
          </Box>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#6B7280",
              mb: set.description ? 0.4 : 0,
            }}
          >
            {count} item{count === 1 ? "" : "s"}
          </Typography>
          {set.description && (
            <Typography
              sx={{
                fontSize: "0.76rem",
                color: "#9CA3AF",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {set.description}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onToggleExpand(set)}
          endIcon={
            open ? (
              <ExpandLessIcon sx={{ fontSize: 15 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 15 }} />
            )
          }
          sx={{
            fontSize: "0.74rem",
            textTransform: "none",
            py: 0.3,
            px: 1.2,
            minWidth: 0,
            color: "#374151",
            borderColor: "#E5E7EB",
            borderRadius: 1,
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {open ? "Collapse" : "Expand"}
        </Button>
      </Box>

      {open && (
        <Box
          sx={{
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#FAFAFA",
            p: 1.5,
          }}
        >
          {state?.status === "loading" && (
            <LoadingBlock label="Checking stock..." />
          )}
          {state?.status === "error" && (
            <ErrorBlock message={state.error} onRetry={() => onRetry(set)} />
          )}
          {state?.status === "loaded" &&
            (lines.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#9CA3AF",
                  textAlign: "center",
                  py: 2,
                }}
              >
                This order set has no items.
              </Typography>
            ) : (
              <>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
                >
                  <Button
                    size="small"
                    onClick={() => onAddAll(set, lines)}
                    disabled={allAdded}
                    sx={{
                      fontSize: "0.74rem",
                      textTransform: "none",
                      py: 0.3,
                      px: 1.2,
                      minWidth: 0,
                      color: "#0E3F66",
                      border: "1px solid #BFDBFE",
                      borderRadius: 1,
                      backgroundColor: "#EFF6FF",
                      "&:hover": { backgroundColor: "#DBEAFE" },
                    }}
                  >
                    {allAdded ? "All Added" : "+ Add all to cart"}
                  </Button>
                </Box>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow>
                        <ColHeader width="28%">Medicine</ColHeader>
                        <ColHeader width="18%">Frequency</ColHeader>
                        <ColHeader width="12%">Qty/Dose</ColHeader>
                        <ColHeader width="10%">Days</ColHeader>
                        <ColHeader width="20%">Availability</ColHeader>
                        <ColHeader width="12%"></ColHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lines.map((line) => (
                        <TableRow
                          key={line.item_id}
                          hover
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                        >
                          <BodyCell sx={{ fontWeight: 600 }}>
                            {line.item_name}
                          </BodyCell>
                          <BodyCell
                            sx={{ fontSize: "0.78rem", color: "#374151" }}
                          >
                            {line.default_frequency_code || "—"}
                          </BodyCell>
                          <BodyCell
                            sx={{ fontSize: "0.78rem", color: "#374151" }}
                          >
                            {line.default_quantity_per_dose ?? "—"}
                          </BodyCell>
                          <BodyCell
                            sx={{ fontSize: "0.78rem", color: "#374151" }}
                          >
                            {line.default_days ?? "—"}
                          </BodyCell>
                          <BodyCell>
                            {line.warning ? (
                              <WarningPill
                                label={line.warning}
                                severe={!line.available_quantity}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "#374151",
                                }}
                              >
                                {line.available_quantity ?? "—"}
                              </Typography>
                            )}
                          </BodyCell>
                          <BodyCell>
                            {isInCart(line.item_id) ? (
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  color: "#21cdc0",
                                  fontWeight: 600,
                                }}
                              >
                                Added
                              </Typography>
                            ) : (
                              <Button
                                size="small"
                                startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                                onClick={() => onAddOne(line)}
                                sx={{
                                  fontSize: "0.72rem",
                                  textTransform: "none",
                                  py: 0.2,
                                  px: 1,
                                  minWidth: 0,
                                  color: "#0E3F66",
                                  border: "1px solid #BFDBFE",
                                  borderRadius: 1,
                                  backgroundColor: "#EFF6FF",
                                  "&:hover": { backgroundColor: "#DBEAFE" },
                                }}
                              >
                                Add
                              </Button>
                            )}
                          </BodyCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ))}
        </Box>
      )}
    </Paper>
  );
};

// ─── Pharmacy orders: POST get_ipd_prescriptions_by_admission/ ───────────────
// Confirmed response shape: { status, data: [{ order_no, medicine_name,
// generic_medicine_names, doctor_name, status, created_at, ... }], is_valid }
// — a flat list, one row per prescribed medicine (not nested under a
// per-Sign-&-Send `medicines[]`). Note `rx.name` is the *patient's* name, not
// the medicine — don't fall back to it for `service`.
const buildPharmacyOrderRow = (
  rx,
  fallbackDoctor,
  fallbackPharmacy,
  index,
) => ({
  // React key only — deliberately not order-number shaped, because it is not one.
  // The order number the user sees and the one write endpoints receive is `orderNo`.
  id: rx?.order_no || `pharmacy-row-${rx?.id ?? index}`,
  // Issued by the backend. Left empty when the response carried none — nothing here
  // invents one, so the row shows "—" and its status can't be changed.
  orderNo: rx?.order_no || "",
  type: "Pharmacy",
  service: rx?.medicine_name || rx?.generic_medicine_names || "—",
  cpt: "",
  doctor: rx?.doctor_name || fallbackDoctor || "",
  priority: rx?.priority || "Routine",
  ordered: rx?.created_at || "",
  status: PHARMACY_STATUS_FROM_API[rx?.status] || rx?.status || "Sent",
  dept: fallbackPharmacy || "Internal Pharmacy",
});

// Backend has been seen returning the same prescribed medicine twice for one admission
// (identical name + timestamp) — collapse exact repeats so Orders doesn't render duplicates.
const dedupePharmacyRows = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = `${row.service}|${row.ordered}|${row.doctor}|${row.dept}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const extractPharmacyOrdersFromPrescriptionsResponse = (
  data,
  fallbackDoctor,
  fallbackPharmacy,
) => {
  const rows = Array.isArray(data?.data) ? data.data : null;
  if (!rows) return null;
  return dedupePharmacyRows(
    rows.map((rx, index) =>
      buildPharmacyOrderRow(rx, fallbackDoctor, fallbackPharmacy, index),
    ),
  );
};

// [INVESTIGATIONS_API] get_ipd_lab_orders_by_admission/ and get_ipd_radiology_orders_by_admission/
// are separate, category-scoped endpoints — every row from the lab endpoint is "Lab", every row
// from the radiology endpoint is "Radiology", no keyword-guessing needed like a single shared
// endpoint would require.
// Backend reports the order's pre-collection workflow status as "NEW" or
// "PAID" (billing-side, not the Ordered→Completed lab/radiology lifecycle).
// Workflow values written from the lab / phlebotomy / radiology dashboards
// (Sample Acknowledged, Sample Collected, Sample Sent, Started, …) must pass
// through so this Orders tab matches those screens for the same test.
const BILLING_PLACEMENT_STATUSES = new Set(["NEW", "PAID"]);

const normalizeInvestigationOrderStatus = (raw) => {
  if (raw == null || raw === "") return "Ordered";
  const value = String(raw).trim();
  if (!value) return "Ordered";
  const upper = value.toUpperCase();
  if (BILLING_PLACEMENT_STATUSES.has(upper)) return "Ordered";
  if (upper === "IN PROGRESS" || upper === "IN_PROGRESS") return "In Progress";
  return value;
};

const buildLabOrderRow = (order, fallbackDoctor, type, dept, index) => ({
  // React key only — see buildPharmacyOrderRow. This used to be
  // `${type.toUpperCase()}-${medical_service_id}`, which read exactly like a real order
  // number ("RADIOLOGY-3063") and got posted to update_lab_test_status/ as one.
  id:
    order?.order_no ||
    `${type.toLowerCase()}-row-${order?.medical_service_id ?? order?.sample_id ?? index}`,
  orderNo: order?.order_no || "",
  type,
  service: order?.service_name || "—",
  cpt: "",
  doctor: order?.doctor_name || fallbackDoctor || "",
  priority: order?.priority || "Routine",
  ordered: order?.ordered_time || "",
  status: normalizeInvestigationOrderStatus(
    order?.status ||
      order?.lab_status ||
      order?.test_status ||
      order?.current_status,
  ),
  results: normalizeLabResultRows(
    order?.results ||
      order?.test_results ||
      order?.parameters ||
      order?.lab_results,
  ),
  dept,
});

// [INVESTIGATIONS_API] Shared response parser for both get_ipd_lab_orders_by_admission/ and
// get_ipd_radiology_orders_by_admission/ — same envelope, populates Orders tab rows from
// add_patient_lab_tests/ orders actually placed for this admission, same pattern as
// extractPharmacyOrdersFromPrescriptionsResponse for Pharmacy rows.
const extractLabOrdersFromResponse = (data, fallbackDoctor, type, dept) => {
  const orders = Array.isArray(data?.orders)
    ? data.orders
    : Array.isArray(data?.data?.orders)
      ? data.data.orders
      : null;
  if (!orders) return null;
  return orders.map((order, index) =>
    buildLabOrderRow(order, fallbackDoctor, type, dept, index),
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const OrderManagementDrawer = ({
  open,
  onClose,
  patient,
  onStopMedication,
}) => {
  const [tab, setTab] = useState(0);
  const [activeOrders, setActiveOrders] = useState([]);
  const [stopTarget, setStopTarget] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [reportOrder, setReportOrder] = useState(null);

  // Prescription cart is shared by the Pharmacy and Order Sets tabs — items
  // added from either place land in the same cart for one Sign & Send.
  const [pharmacy, setPharmacy] = useState("Internal Pharmacy");
  const [cartItems, setCartItems] = useState([]);
  const [medicationOrderSchema, setMedicationOrderSchema] = useState(null);
  const [addingMedicineId, setAddingMedicineId] = useState(null);
  const [isSigningPrescription, setIsSigningPrescription] = useState(false);
  const medicationOrderSchemaRef = useRef(null);
  const structuresFetchRef = useRef(null);

  // [ORDER_STATUS_API] Statuses this session wrote and the server accepted, keyed by row id.
  // The by-admission read endpoints can lag a just-written status (the same indexing delay
  // the Refresh tooltip warns about), so leaving a tab and coming back — which refires the
  // loaders below — would otherwise walk a just-cancelled row back to its previous status.
  const persistedStatusRef = useRef(new Map());
  const applyPersistedStatuses = useCallback(
    (rows) =>
      rows.map((row) => {
        const persisted = persistedStatusRef.current.get(row.id);
        return persisted && persisted !== row.status
          ? { ...row, status: persisted }
          : row;
      }),
    [],
  );

  const loadMedicationOrderSchema = useCallback(async () => {
    if (medicationOrderSchemaRef.current)
      return medicationOrderSchemaRef.current;
    if (structuresFetchRef.current) return structuresFetchRef.current;

    structuresFetchRef.current = axios
      .get(`${API_BASE}care_plan_structures/`, { headers: apiHeaders() })
      .then((response) => {
        const structures = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const schema = extractOrdersMedicationSchema(structures);
        medicationOrderSchemaRef.current = schema;
        setMedicationOrderSchema(schema);
        return schema;
      })
      .catch((err) => {
        console.error("Failed to load care plan structures:", err);
        toast.error("Could not load medication order form.");
        return null;
      })
      .finally(() => {
        structuresFetchRef.current = null;
      });

    return structuresFetchRef.current;
  }, []);

  useEffect(() => {
    if (open && tab === 1) {
      loadMedicationOrderSchema();
    }
  }, [open, tab, loadMedicationOrderSchema]);

  const addMedicineToCart = (medicine) => {
    const id = String(medicine.id || medicine.item_id);
    if (!id || id === "null" || id === "undefined") return;
    setCartItems((prev) => {
      // Check if medicine is already in cart by name (since different APIs use different IDs)
      const alreadyInCart = prev.some(
        (c) => c.name && medicine.name &&
        c.name.trim().toLowerCase() === medicine.name.trim().toLowerCase()
      );
      return alreadyInCart ? prev : [...prev, createCartItem(medicine)];
    });
  };

  const handleAddMedicine = useCallback(
    async (medicine) => {
      const id = String(medicine?.id || medicine?.item_id || "");
      if (!id) return;
      setAddingMedicineId(id);
      try {
        const schema = await loadMedicationOrderSchema();
        if (!schema) return;
        addMedicineToCart(medicine);
      } finally {
        setAddingMedicineId(null);
      }
    },
    [loadMedicationOrderSchema],
  );
  const addSetLineToCart = (line) => {
    const id = String(line.item_id);
    setCartItems((prev) =>
      prev.find((c) => c.id === id)
        ? prev
        : [...prev, createCartItemFromSetLine(line)],
    );
  };
  const removeFromCart = (id) =>
    setCartItems((prev) => prev.filter((c) => c.id !== id));
  const updateCartItem = (id, field, value) =>
    setCartItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  const isInCart = (id) => cartItems.some((c) => c.id === String(id));

  // [PHARMACY_RX] POST get_ipd_prescriptions_by_admission/ — populates the
  // Pharmacy rows in the Orders tab from whatever has actually been sent for
  // this admission. Only fires when the Orders tab is open (tab === 0), not
  // from the Pharmacy tab or Sign & Send.
  const loadPharmacyOrders = useCallback(async () => {
    const admissionId = patient?.admissionId || patient?.id;
    if (!admissionId) return;
    try {
      const res = await axios.post(
        `${API_BASE}get_ipd_prescriptions_by_admission/`,
        { admission_id: admissionId },
        { headers: apiHeaders() },
      );
      if (res.data?.is_valid === false) {
        throw new Error(res.data?.message || "Request rejected by server");
      }
      const fallbackDoctor = patient?.doctor_name || patient?.consultant || "";
      const rows = extractPharmacyOrdersFromPrescriptionsResponse(
        res.data,
        fallbackDoctor,
        pharmacy,
      );
      if (rows) {
        setActiveOrders((prev) => [
          ...prev.filter((o) => o.type !== "Pharmacy"),
          ...applyPersistedStatuses(rows),
        ]);
      } else {
        console.warn(
          "[PHARMACY_RX] Unrecognized get_ipd_prescriptions_by_admission response shape:",
          res.data,
        );
      }
    } catch (err) {
      console.error(
        "Failed to load pharmacy orders:",
        err.response?.data || err.message,
      );
    }
  }, [patient, pharmacy, applyPersistedStatuses]);

  // [INVESTIGATIONS_API] POST get_ipd_lab_orders_by_admission/ — populates Lab rows in the
  // Orders tab from add_patient_lab_tests/ orders actually placed for this admission, whether
  // they came from Care Plan → Orders → Investigation or from this drawer's own Investigations
  // tab. Only fires when the Orders tab is open (tab === 0).
  const loadLabOrders = useCallback(async () => {
    const admissionId = patient?.admissionId || patient?.id;
    if (!admissionId) return;
    try {
      const res = await axios.post(
        `${API_BASE}get_ipd_lab_orders_by_admission/`,
        { admission_id: String(admissionId) },
        { headers: apiHeaders() },
      );
      if (res.data?.is_valid === false) {
        throw new Error(res.data?.message || "Request rejected by server");
      }
      // Response carries doctor_name at the top level, not per-order — prefer that over patient data.
      const fallbackDoctor =
        res.data?.doctor_name ||
        patient?.doctor_name ||
        patient?.consultant ||
        "";
      const rows = extractLabOrdersFromResponse(
        res.data,
        fallbackDoctor,
        "Lab",
        "Laboratory",
      );
      if (rows) {
        setActiveOrders((prev) => [
          ...prev.filter((o) => o.type !== "Lab"),
          ...applyPersistedStatuses(rows),
        ]);
      } else {
        console.warn(
          "[INVESTIGATIONS_API] Unrecognized get_ipd_lab_orders_by_admission response shape:",
          res.data,
        );
      }
    } catch (err) {
      console.error(
        "Failed to load lab orders:",
        err.response?.data || err.message,
      );
    }
  }, [patient, applyPersistedStatuses]);

  // [INVESTIGATIONS_API] POST get_ipd_radiology_orders_by_admission/ — same idea as
  // loadLabOrders, but for the Radiology-category rows (same add_patient_lab_tests/ write,
  // separate read endpoint). Only fires when the Orders tab is open (tab === 0).
  const loadRadiologyOrders = useCallback(async () => {
    const admissionId = patient?.admissionId || patient?.id;
    if (!admissionId) return;
    try {
      const res = await axios.post(
        `${API_BASE}get_ipd_radiology_orders_by_admission/`,
        { admission_id: String(admissionId) },
        { headers: apiHeaders() },
      );
      if (res.data?.is_valid === false) {
        throw new Error(res.data?.message || "Request rejected by server");
      }
      // Response carries doctor_name at the top level, not per-order — prefer that over patient data.
      const fallbackDoctor =
        res.data?.doctor_name ||
        patient?.doctor_name ||
        patient?.consultant ||
        "";
      const rows = extractLabOrdersFromResponse(
        res.data,
        fallbackDoctor,
        "Radiology",
        "Radiology",
      );
      if (rows) {
        setActiveOrders((prev) => [
          ...prev.filter((o) => o.type !== "Radiology"),
          ...applyPersistedStatuses(rows),
        ]);
      } else {
        console.warn(
          "[INVESTIGATIONS_API] Unrecognized get_ipd_radiology_orders_by_admission response shape:",
          res.data,
        );
      }
    } catch (err) {
      console.error(
        "Failed to load radiology orders:",
        err.response?.data || err.message,
      );
    }
  }, [patient, applyPersistedStatuses]);

  useEffect(() => {
    if (open && tab === 0) {
      const loadAllOrders = async () => {
        await Promise.all([
          loadPharmacyOrders(),
          loadLabOrders(),
          loadRadiologyOrders(),
        ]);
      };
      loadAllOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, patient?.admissionId]);

  // [PHARMACY_RX] POST add_ipd_prescription/ — one call per Sign & Send,
  // carrying every medicine currently in the shared cart. medicines[] entries
  // are { medicine_name, generic_medicine_names, details: { dose, frequency,
  // duration } } — the flat { name, dose, frequency, duration } shape is
  // rejected ("Each medicine must be an object carrying a medicine_name…").
  //   • dose comes from the same M/A/E/N summary already shown on the cart
  //     row (formatDoseSummary) — the cart tracks per-slot quantities, not a
  //     single "1 tab" string, so this is the closest honest equivalent.
  //   • allergies is a flat string[] — registration only captures allergies
  //     as one free-text field (see IpdDataContext's getAllergiesList), so
  //     there's no severity/reaction data to send per-allergy.
  // [TIMELINE] POST care_plans/ — the IPD Timeline is rendered from
  // GET care_plans/?admission_id= (see PatientTimelineModal), so an order only appears
  // there if a care plan row exists for it. Care Plan → Orders got that for free because
  // it saves through the care plan form; orders placed from this drawer went straight to
  // add_ipd_prescription/ / add_patient_lab_tests/ and so were invisible on the timeline.
  //
  // Payload mirrors what PatientDetailsPanel's handleAddCarePlan sends for the "orders"
  // activity: one row whose data holds the three order groups plus the __timeline_* keys
  // the timeline groups a single save by.
  //
  // Deliberately best-effort: the order itself is already placed by the time this runs, so
  // a timeline failure is logged and toasted as a warning, never surfaced as a failed order.
  const logOrdersToTimeline = useCallback(
    async ({ medicationOrders = [], testOrders = [] }) => {
      const admissionId = patient?.admissionId || patient?.id;
      if (!admissionId) return;
      if (medicationOrders.length === 0 && testOrders.length === 0) return;
      try {
        const schema = await loadMedicationOrderSchema();
        const carePlanId = schema?.carePlanId;
        if (!carePlanId) {
          console.warn(
            "[TIMELINE] Skipped care_plans/ — no Orders structure id available.",
          );
          return;
        }
        const groupId = `cp-${carePlanId}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
        const createdAt = new Date().toISOString();
        const res = await axios.post(
          `${API_BASE}care_plans/`,
          {
            admission_id: Number(admissionId),
            care_plans: [
              {
                care_plan_id: carePlanId,
                data: {
                  medication_order: medicationOrders,
                  test_order: testOrders,
                  nutrition_order: [],
                  __timeline_group_id: groupId,
                  __timeline_created_at: createdAt,
                  __timeline_sort_ts: Date.now(),
                },
              },
            ],
          },
          { headers: apiHeaders() },
        );
        if (res.data?.is_valid === false || res.data?.status === "Failed") {
          throw new Error(
            res.data?.message || "Care plan save was rejected by server",
          );
        }
      } catch (err) {
        console.error(
          "[TIMELINE] Could not add order to IPD timeline:",
          err.response?.data || err.message,
        );
        toast.warn(
          "Order placed, but it could not be added to the IPD timeline.",
        );
      }
    },
    [patient, loadMedicationOrderSchema],
  );

  const handleSignAndSend = useCallback(async () => {
    if (cartItems.length === 0) return;
    const missingRequired = cartItems.find(
      (item) =>
        !String(item.priority || "").trim() ||
        !String(item.route || "").trim() ||
        !String(item.frequency || "").trim() ||
        item.duration === "" ||
        item.duration == null ||
        !String(item.durationUnit || "").trim(),
    );
    if (missingRequired) {
      toast.error(
        "Please fill Priority, Route, Frequency, Duration and Unit for every medicine.",
      );
      return;
    }
    const patientIdentifier =
      patient?.patient_id || patient?.pat_id || patient?.patientId || "";
    if (!patientIdentifier) {
      toast.error("Cannot send prescription — missing patient ID.");
      return;
    }
    setIsSigningPrescription(true);
    try {
      await axios.post(
        `${API_BASE}add_ipd_prescription/`,
        {
          patient_id: patientIdentifier,
          ipd_flag: true,
          provisional_diagnosis:
            patient?.provisionalDiagnosis || patient?.condition || "",
          final_diagnosis: patient?.finalDiagnosis || patient?.condition || "",
          status: "Sent",
          allergies: patient?.allergies || [],
          // details is stored verbatim and handed straight to
          // generate_medication_schedule, so duration MUST go over numeric with
          // duration_unit beside it. The "5 Day(s)" string this used to send
          // failed float() server-side and scheduled every order for one day at
          // dose 1, whatever the doctor entered. dose keeps riding along for the
          // pharmacy screen, which reads it for display.
          medicines: cartItems.map((item) => ({
            medicine_name: item.name,
            generic_medicine_names: resolveGenericName(item.generic, item.name),
            details: buildMedicationDetails({
              ...item,
              duration_unit: item.durationUnit,
              dose: formatDoseSummary(item),
            }),
          })),
        },
        { headers: apiHeaders() },
      );
      toast.success("Prescription sent to pharmacy.");
      // [TIMELINE] After the prescription is accepted, not before — the timeline should
      // never show a medication the pharmacy never received. Field names match what
      // CarePlanActivityModal's own readers expect (medication.drug_name, priority).
      await logOrdersToTimeline({
        medicationOrders: cartItems.map((item) => ({
          medication: {
            drug_name: item.name,
            generic_name: resolveGenericName(item.generic, item.name),
            dose: formatDoseSummary(item),
            frequency: item.frequency,
            duration: item.duration || "",
            duration_unit: item.durationUnit || "",
            route: item.route || "",
          },
          priority: toWirePriority(item.priority),
        })),
      });
      setCartItems([]);
      // Reload orders to show the newly placed order at the top
      loadPharmacyOrders();
    } catch (err) {
      console.error(
        "Failed to send prescription:",
        err.response?.data || err.message,
      );
      toast.error("Could not send prescription. Please try again.");
    } finally {
      setIsSigningPrescription(false);
    }
  }, [cartItems, patient, loadPharmacyOrders, logOrdersToTimeline]);

  const patientName = patient?.patient_name || patient?.name || "Patient";
  const uhid = patient?.patient_id || patient?.uhid || "";
  const age = patient?.age || "";
  const gender = patient?.gender ? patient.gender.charAt(0).toUpperCase() : "";
  const ward =
    patient?.bed_type_name || patient?.ward || "";
  const bed = patient?.bedNo || patient?.bed_id || patient?.bed || "";
  const diagnosis = patient?.diagnosis || patient?.condition || "—";
  const admitted = patient?.admissionDate || patient?.admission_date || "";

  const handleStopConfirm = async (order, reason, comment) => {
    const patientId = patient?.id || patient?.patient_id;
    if (
      onStopMedication &&
      patientId &&
      order.carePlanId &&
      order.medicationId
    ) {
      await onStopMedication(patientId, order.carePlanId, order.medicationId);
    }
    setActiveOrders((prev) =>
      prev.map((o) =>
        o.id === order.id
          ? {
              ...o,
              status: "Stopped",
              stopReason: reason,
              stopComment: comment,
            }
          : o,
      ),
    );
    toast.success(
      `${order.service} has been stopped. Nursing will discontinue further doses.`,
    );
    setStopTarget(null);
  };

  // [ORDER_STATUS_API] POST update_lab_test_status/ (admission_id as string) or
  // update_ipd_prescription_status/ (admission_id as number) — exact payload shapes as given.
  // Optimistically updates the row, rolls back on failure.
  const handleOrderStatusChange = useCallback(
    async (order, newStatus) => {
      const admissionId = patient?.admissionId || patient?.id;
      if (!admissionId) return;
      // No order number means the backend never issued one for this row — there is nothing to
      // address the write to, so refuse it rather than post a locally-made-up value that the
      // server can't match (which used to look like it worked until the next refetch).
      if (!order.orderNo) {
        toast.error(
          "This order has no order number from the server — its status can't be updated.",
        );
        return;
      }
      const prevStatus = order.status;
      setActiveOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o)),
      );
      try {
        let res = null;
        if (order.type === "Lab" || order.type === "Radiology") {
          res = await axios.post(
            `${API_BASE}update_lab_test_status/`,
            {
              admission_id: String(admissionId),
              order_no: order.orderNo,
              status: newStatus,
            },
            { headers: apiHeaders() },
          );
        } else if (order.type === "Pharmacy") {
          res = await axios.post(
            `${API_BASE}update_ipd_prescription_status/`,
            // order_no, not order_id — the latter is ignored, and the endpoint then refuses the
            // call outright ("admission-wide stamping is not supported") rather than stamping
            // every prescription on the admission. prescription_ids is the other accepted key,
            // for when one order_no covers several medicine rows and only some are being changed.
            {
              admission_id: Number(admissionId),
              order_no: order.orderNo,
              status: PHARMACY_STATUS_TO_API[newStatus] || newStatus,
            },
            { headers: apiHeaders() },
          );
        }
        // These endpoints answer 200 with a Failed/is_valid envelope when they decline the
        // write — without this check a declined cancel toasts success and only reveals itself
        // when a refetch brings the old status back.
        if (res?.data?.is_valid === false || res?.data?.status === "Failed") {
          throw new Error(res.data?.message || "Request rejected by server");
        }
        persistedStatusRef.current.set(order.id, newStatus);
        toast.success(`Status updated to ${newStatus}.`);
      } catch (err) {
        console.error(
          "Failed to update order status:",
          err.response?.data || err.message,
        );
        toast.error("Could not update status. Please try again.");
        setActiveOrders((prev) =>
          prev.map((o) =>
            o.id === order.id ? { ...o, status: prevStatus } : o,
          ),
        );
      }
    },
    [patient],
  );

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#fff",
        zIndex: ORDER_DRAWER_Z_INDEX,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ backgroundColor: "#0E3F66", color: "#fff", flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 3,
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "#fff", mr: 1.5, p: 0.5 }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
            <Typography
              sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)" }}
            >
              Workbench /
            </Typography>
            <Typography
              sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#fff" }}
            >
              Order Management
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 4, px: 3, py: 1.5 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: "#21cdc0",
                fontSize: "0.85rem",
                fontWeight: 700,
              }}
            >
              {patientName
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")}
            </Avatar>
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#fff" }}
              >
                {patientName}
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}
              >
                {uhid}
                {age && ` · ${age} Y`}
                {gender && ` · ${gender}`}
              </Typography>
            </Box>
          </Box>
          {[
            [
              "WARD / BED",
              ward && bed ? `${ward} · ${bed}` : ward || bed || "—",
            ],
            ["DIAGNOSIS", diagnosis],
            ["ADMITTED", admitted || "—"],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{ borderLeft: "1px solid rgba(255,255,255,0.15)", pl: 3 }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  color: "rgba(255,255,255,0.5)",
                  letterSpacing: 0.5,
                  mb: 0.3,
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: "0.8rem", color: "#fff", fontWeight: 500 }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Tab bar ── */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #E5E7EB",
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setDetailOrder(null);
          }}
          sx={{
            "& .MuiTab-root": {
              fontSize: "0.82rem",
              textTransform: "none",
              fontWeight: 500,
              minHeight: 46,
              px: 2,
            },
            "& .Mui-selected": { fontWeight: 700, color: "#0E3F66 !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#0E3F66" },
          }}
        >
          <Tab label="Orders" />
          <Tab label="Pharmacy" />
          <Tab label="Investigations" />
        </Tabs>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {tab === 0 && (
            <Tooltip title="Refresh — backend can take a moment to index a just-placed order">
              <IconButton
                size="small"
                onClick={() => {
                  loadPharmacyOrders();
                  loadLabOrders();
                  loadRadiologyOrders();
                }}
                sx={{
                  color: "#374151",
                  border: "1px solid #E5E7EB",
                  borderRadius: 1.5,
                }}
              >
                <RefreshIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            variant="contained"
            onClick={() => setTab(2)}
            sx={{
              fontSize: "0.78rem",
              textTransform: "none",
              backgroundColor: "#0E3F66",
              "&:hover": { backgroundColor: "#0a2e4d" },
            }}
          >
            New Order
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ flex: 1, overflow: "hidden", display: "flex" }}>
        <Box
          sx={{
            flex: 1,
            overflowY: tab === 1 || tab === 2 ? "hidden" : "auto",
            overflowX: "hidden",
            p: tab === 1 || tab === 2 ? 0 : 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tab === 0 && (
            <OrdersTab
              activeOrders={activeOrders}
              previousOrders={PREVIOUS_ORDERS}
              onCancelClick={(o) => handleOrderStatusChange(o, "Cancelled")}
              onStopClick={(o) => setStopTarget(o)}
              onViewClick={(o) => setDetailOrder(o)}
              onViewReport={(o) => setReportOrder(o)}
              onStatusChange={handleOrderStatusChange}
            />
          )}
          {tab === 1 && (
            <PharmacyTab
              cartItems={cartItems}
              pharmacy={pharmacy}
              setPharmacy={setPharmacy}
              onAdd={handleAddMedicine}
              onAddSetLineToCart={addSetLineToCart}
              onRemove={removeFromCart}
              onUpdate={updateCartItem}
              isInCart={isInCart}
              addingMedicineId={addingMedicineId}
              medicationSchema={medicationOrderSchema}
              onSignAndSend={handleSignAndSend}
              isSigning={isSigningPrescription}
            />
          )}
          {tab === 2 && (
            <InvestigationsTab
              patient={patient}
              activeOrders={activeOrders}
              onOrdersChanged={() => {
                if (tab === 0) {
                  loadLabOrders();
                  loadRadiologyOrders();
                }
              }}
              onLogToTimeline={logOrdersToTimeline}
            />
          )}
        </Box>
        {detailOrder && tab !== 1 && (
          <OrderDetailPanel
            order={detailOrder}
            onClose={() => setDetailOrder(null)}
          />
        )}
      </Box>

      <StopDialog
        open={Boolean(stopTarget)}
        order={stopTarget}
        onClose={() => setStopTarget(null)}
        onConfirm={handleStopConfirm}
      />
      <LabOrderReportDialog
        order={reportOrder}
        patientName={patientName}
        uhid={uhid}
        patient={patient}
        onClose={() => setReportOrder(null)}
      />
    </Box>
  );
};

export default OrderManagementDrawer;
